import { useState, useEffect, useCallback, useMemo, useRef, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowPathIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
  UserPlusIcon,
  UserCircleIcon,
  TrashIcon,
  Squares2X2Icon,
  ListBulletIcon,
  ChartBarIcon,
  NoSymbolIcon,
} from '@heroicons/react/24/outline';
import { useNode } from '../contexts/NodeContext';
import { getNodeOccupancySeries, getNodeModelEvents, getNodeModelEvent, getNodeModelEventImage, getNodeGroups, getNodeCameras, getGroupCameras, getNodePersons, getNodePersonsPage, registerNodePersonFromEvent, linkNodePersonFromEvent, deleteNodePerson, registerNodeEmployeeFromEvent, getNodePersonFaces, unlinkNodePersonFace, markNodePersonAsEmployee, unmarkNodePersonAsEmployee, uploadNodePersonPhotos, blacklistNodePerson, unblacklistNodePerson, registerNodePersonWithPhotos } from '../services/api.service';
import { useAuth } from '../contexts/AuthContext';
import { useCameraMedia } from '../hooks/useCameraMedia';
import {
  EventDetail,
  AppearancePanel,
  SubjectBreakdown,
  SubjectOverlay,
  subjectAttributeFacts,
  type EventSubject,
  EventListCards,
  OccupancyReport,
  Modal,
  RecordPager,
  RecordFullPageButton,
  SegmentedToggle,
  RegisteredPersonCard,
  RegisteredPeopleToolbar,
  PeopleLoadMore,
  EnrolledFacesList,
  VehicleBlacklistPanel,
  PersonBlacklistDialog,
  PersonRegisterDialog,
  PersonWatchlistToggle,
  type PersonWatchlistValue,
  type PersonRegisterValue,
  eventTypeLabel,
  reportModelLabel,
  reportModelNote,
  reportModelSlug,
  snapshotTypeLabel,
  snapshotTypeRank,
  splitEventFacts,
  PageTitle,
  PageWithFilters,
  FilterColumn,
  FilterSection,
  FilterCheck,
  FilterSearch,
  FILTER_CONTROL,
  useFilterColumn,
  SnapshotGallery,
  type EventFact,
  type GalleryImage,
  type PeopleRegistryFilter,
  AI_ANALYTICS_COLUMNS,
} from '@trinetra/shared';



/* ─── Types ─── */
interface ColumnDef {
  key: string;
  label: string;
  type?: string;
  /** Part of the record but not of the table — folded into another cell there. */
  detailOnly?: boolean;
  /** Long free text: full-width and wrapped in the record's Details, never cut off at "…". */
  wide?: boolean;
  // `row` is needed by columns whose value lives inside a jsonb blob
  // (e.g. People reads metadata.current_occupancy). Optional so the
  // existing single-arg renderers keep type-checking unchanged.
  render?: (v: any, row?: any) => any;
}

/* ─── Constants ─── */
const AI_MODELS = [
  { key: 'person', label: 'Person Detection' },
  { key: 'person_count', label: 'Person Count' },
  { key: 'intrusion', label: 'Intrusion Detection' },
  { key: 'face', label: 'FRS Face Recognition' },
  { key: 'vehicle_crossing', label: 'Vehicle Detection' },
  { key: 'ppe_violation', label: 'PPE Violation' },
  { key: 'theft', label: 'Theft Detection' },
  { key: 'fall', label: 'Fall Detection' },
  { key: 'fire_smoke', label: 'Fire & Smoke Detection' },
  { key: 'ocr', label: 'Text & Plate Reads (OCR)' },
  { key: 'ai_analytics', label: 'AI Analytics Events' },
];

/** Rows-per-page choices offered in the filter bar (index 1 = 25 is the default). */
const EVENTS_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

/**
 * How many registered people the drawer asks for at a time. Kept at the old flat fetch's 100 so
 * a site that was seeing its whole registry in one go still does — the difference is that past
 * 100 the list now says so and offers the rest, instead of ending as if that were everybody.
 *
 * It is also the node's ceiling: `clampLimit` in personsController pins /persons?limit to 100,
 * so "show me more" has to be a further PAGE. Asking for 200 would quietly return 100 again and
 * the Load-more button would sit there doing nothing.
 */
const REGISTRY_PAGE = 100;

/** How far the unfiltered roster walk goes — 500 people is a directory, not a scroll. */
const ROSTER_MAX_PAGES = 5;

/** Parse a jsonb column that may arrive as an object or as a JSON string. */
function jsonish(v: any): any {
  if (!v) return null;
  if (typeof v === 'string') { try { return JSON.parse(v); } catch { return null; } }
  return typeof v === 'object' ? v : null;
}

/* ─── Per-model column definitions ─── */
const MODEL_COLUMNS: Record<string, ColumnDef[]> = {
  // The person model mixes sub-types (crossing, person_detection, person_visible,
  // loitering) in ONE table, and they populate different fields: a crossing carries
  // identity + direction, a person_detection carries an occupancy count and nothing
  // else. Every sub-type therefore has some '—' cells — that is honest, not a bug.
  // 'People' exists so a person_detection row shows its one real measurement instead
  // of being an entirely empty line.
  person: [
    { key: 'image_paths', label: 'Images', type: 'images' },
    { key: 'event_timestamp', label: 'Detected At', type: 'datetime', render: (v) => formatTime(v) },
    { key: 'camera_id', label: 'Camera', type: 'camera' },
    { key: 'sub_type', label: 'Event Type' },
    // Identity + In/Out removed on request. In/Out was dead for every sub-type
    // (0 of 1546 rows populated). Identity is still populated for face_recognized
    // rows — those now show their name only in the row-detail panel.
    { key: 'metadata', label: 'People', render: (v) => {
      const m = jsonish(v);
      return m?.current_occupancy != null ? String(m.current_occupancy) : '—';
    } },
    // The AI module sends no confidence for person_detection/person_visible, and the
    // column is NOT NULL, so those rows store 0. Render 0 as '—' rather than "0%",
    // which would read as "the model was 0% sure" instead of "no value was sent".
    { key: 'confidence', label: 'Confidence', type: 'confidence', render: (v) => v ? `${(v * 100).toFixed(0)}%` : '—' },
  ],
  person_count: [
    { key: 'image_paths', label: 'Images', type: 'images' },
    { key: 'event_timestamp', label: 'Time', type: 'datetime', render: (v) => formatTime(v) },
    { key: 'camera_id', label: 'Camera', type: 'camera' },
    { key: 'current_occupancy', label: 'Count', render: (v) => v != null ? String(v) : '0' },
    { key: 'average_occupancy', label: 'Avg', render: (v) => v != null ? String(v) : '—' },
    { key: 'max_occupancy', label: 'Max', render: (v) => v != null ? String(v) : '—' },
    { key: 'interval_seconds', label: 'Interval', render: (v) => v ? `${Number(v).toFixed(0)}s` : '—' },
  ],
  intrusion: [
    { key: 'image_paths', label: 'Images', type: 'images' },
    { key: 'event_timestamp', label: 'Detected At', type: 'datetime', render: (v) => formatTime(v) },
    { key: 'camera_id', label: 'Camera', type: 'camera' },
    { key: 'sub_type', label: 'Event Type' },
    // WHAT breached the zone. Populated all along and never shown, though it is the
    // fact that decides whether anyone needs to go and look.
    { key: 'object_type', label: 'Object', render: (v) => v ? v.charAt(0).toUpperCase() + v.slice(1) : '—' },
    { key: 'track_id', label: 'Track ID', render: (v) => v ? `TRK-${v}` : '—' },
    { key: 'roi_zone', label: 'Zone' },
    // The column is time_in_zone_seconds, not duration_seconds — this read the wrong
    // key and rendered "—" on every row since the report was written.
    { key: 'time_in_zone_seconds', label: 'Time in Zone', render: (v) => v ? `${Number(v).toFixed(1)} sec` : '—' },
    { key: 'confidence', label: 'Confidence', type: 'confidence', render: (v) => v ? `${(v * 100).toFixed(0)}%` : '—' },
    { key: 'direction', label: 'Direction', detailOnly: true },
  ],
  face: [
    { key: 'image_paths', label: 'Images', type: 'images' },
    { key: 'matched_person_name', label: 'Identity', type: 'face_identity' },
    { key: 'event_timestamp', label: 'Detected At', type: 'datetime', render: (v) => formatTime(v) },
    { key: 'camera_id', label: 'Camera', type: 'camera' },
    { key: 'inout', label: 'In/Out', type: 'inout' },
    { key: 'confidence', label: 'Confidence', type: 'confidence', render: (v) => v ? `${(v * 100).toFixed(0)}%` : '—' },
    { key: 'id', label: 'Action', type: 'face_action' },
    // Shown under the name in the table, so it would be the same string twice as a column
    // of its own — but it is still the id a support ticket quotes, so the record keeps it.
    { key: 'person_id', label: 'Person ID', type: 'person_id', detailOnly: true },
  ],
  vehicle_crossing: [
    { key: 'image_paths', label: 'Images', type: 'images' },
    { key: 'event_timestamp', label: 'Time', type: 'datetime', render: (v) => formatTime(v) },
    { key: 'camera_id', label: 'Camera', type: 'camera' },
    { key: 'event_direction', label: 'Direction', type: 'vehicle_direction' },
    { key: 'class_name', label: 'Vehicle', render: (v) => v ? v.charAt(0).toUpperCase() + v.slice(1) : '—' },
    { key: 'plate_number', label: 'Plate Number', type: 'plate_number' },
    { key: 'ocr_result', label: 'OCR', render: (v) => v || '—' },
    { key: 'plate_status', label: 'Plate Status', type: 'plate_status' },
    { key: 'confidence', label: 'Confidence', type: 'confidence', render: (v) => v ? `${(v * 100).toFixed(0)}%` : '—' },
    { key: 'camera_total_entries', label: 'Entries', render: (v) => v != null ? String(v) : '—' },
    { key: 'camera_total_exits', label: 'Exits', render: (v) => v != null ? String(v) : '—' },
  ],
  ppe_violation: [
    { key: 'image_paths', label: 'Images', type: 'images' },
    { key: 'event_timestamp', label: 'Detected At', type: 'datetime', render: (v) => formatTime(v) },
    { key: 'camera_id', label: 'Camera', type: 'camera' },
    { key: 'violation_type', label: 'Violation', type: 'ppe_violation_type' },
    { key: 'person_confidence', label: 'Person Conf.', type: 'confidence', render: (v) => v ? `${(v * 100).toFixed(0)}%` : '—' },
    { key: 'total_violations', label: 'Violations', render: (v) => v != null ? String(v) : '1' },
    { key: 'total_persons_in_frame', label: 'Persons', render: (v) => v != null ? String(v) : '—' },
    { key: 'total_violators_in_frame', label: 'Violators', render: (v) => v != null ? String(v) : '—' },
  ],
  theft: [
    { key: 'image_paths', label: 'Images', type: 'images' },
    { key: 'event_timestamp', label: 'Detected At', type: 'datetime', render: (v) => formatTime(v) },
    { key: 'camera_id', label: 'Camera', type: 'camera' },
    { key: 'theft_type', label: 'Theft Type', type: 'theft_type' },
    { key: 'object_type', label: 'Object', render: (v) => v ? v.charAt(0).toUpperCase() + v.slice(1) : '—' },
    { key: 'track_id', label: 'Track ID', render: (v) => v ? `TRK-${v}` : '—' },
    { key: 'roi_zone', label: 'Zone' },
    { key: 'duration_seconds', label: 'Duration', render: (v) => v ? `${Number(v).toFixed(1)} sec` : '—' },
    { key: 'confidence', label: 'Confidence', type: 'confidence', render: (v) => v ? `${(v * 100).toFixed(0)}%` : '—' },
  ],
  // Every key here is a real fall_detection_events column (migration 018). The two
  // this replaced — person_confidence and duration_seconds — exist on no fall row
  // and rendered an em dash in every cell of both columns.
  //
  // action_type comes second because it is the one thing the image cannot settle:
  // the detector emits 'person' tracks alongside actual 'fall' verdicts, so the
  // column that says which is the difference between a table of events and a
  // table of alarms.
  fall: [
    { key: 'image_paths', label: 'Images', type: 'images' },
    { key: 'action_type', label: 'Type' },
    { key: 'event_timestamp', label: 'Detected At', type: 'datetime', render: (v) => formatTime(v) },
    { key: 'camera_id', label: 'Camera', type: 'camera' },
    { key: 'person_id', label: 'Person', type: 'person_id' },
    { key: 'confidence', label: 'Confidence', type: 'confidence', render: (v) => v ? `${(v * 100).toFixed(0)}%` : '—' },
    // The kinematics behind the verdict. Detail-only: they are the evidence an
    // operator disputing a fall asks for, and noise in a scanning table.
    { key: 'head_drop_px', label: 'Head Drop', detailOnly: true, render: (v) => v != null ? `${v} px` : '—' },
    { key: 'vel_pk', label: 'Peak Velocity', detailOnly: true, render: (v) => v != null ? String(v) : '—' },
    { key: 'horiz', label: 'Horizontal', detailOnly: true, render: (v) => v == null ? '—' : (v ? 'Yes' : 'No') },
    { key: 'track_id', label: 'Track ID', detailOnly: true, render: (v) => v ? `TRK-${v}` : '—' },
  ],
  // Type comes second, right after the picture. It is the first thing an operator
  // needs from this table — flame and smoke call for different first moves — and
  // the one field that is not obvious from the image on a hazy camera.
  fire_smoke: [
    { key: 'image_paths', label: 'Images', type: 'images' },
    { key: 'detection_type', label: 'Type', type: 'detection_type' },
    { key: 'event_timestamp', label: 'Detected At', type: 'datetime', render: (v) => formatTime(v) },
    { key: 'camera_id', label: 'Camera', type: 'camera' },
    { key: 'roi_zone', label: 'Zone' },
    // Shown as a percentage of frame: raw pixel areas mean nothing across a 720p
    // and a 4K camera, which is why the column stores a ratio.
    { key: 'area_ratio', label: 'Frame Area', render: (v) => v != null ? `${(Number(v) * 100).toFixed(1)}%` : '—' },
    { key: 'duration_seconds', label: 'Duration', render: (v) => v ? `${Number(v).toFixed(1)} sec` : '—' },
    { key: 'confidence', label: 'Confidence', type: 'confidence', render: (v) => v ? `${(v * 100).toFixed(0)}%` : '—' },
    // The DETECTOR's own escalation, not the alert severity — detail view only,
    // since most models never emit it.
    { key: 'severity_level', label: 'Model Severity', detailOnly: true },
    { key: 'track_id', label: 'Track ID', detailOnly: true, render: (v) => v ? `TRK-${v}` : '—' },
    { key: 'frame_count', label: 'Frames', detailOnly: true, render: (v) => v != null ? String(v) : '—' },
  ],
  /**
   * OCR: everything the model READ in one frame — plates (canonicalised) and the other text.
   * The plates and the text are the record; the boxes and per-read confidences stay in the
   * raw `plates` / `other_text` lists, which the detail view shows as-is.
   */
  ocr: [
    { key: 'image_paths', label: 'Images', type: 'images' },
    { key: 'plate_numbers', label: 'Plates', render: (v) => (Array.isArray(v) && v.length ? v.join(', ') : '—') },
    /* Every line the model read, one per row, full width — not one line cut off at "…". From
       the stored lines (`other_text`) where the record has them; `full_text` is the same lines
       joined with " | ", the fallback for anything older. */
    { key: 'full_text', label: 'Text read', wide: true,
      render: (v, row) => {
        const lines = Array.isArray(row?.other_text)
          ? row.other_text.map((t: any) => (typeof t?.text === 'string' ? t.text.trim() : '')).filter(Boolean)
          : (typeof v === 'string' ? v.split(' | ').map((t) => t.trim()).filter(Boolean) : []);
        return lines.length ? lines.join('\n') : '—';
      } },
    { key: 'event_timestamp', label: 'Detected At', type: 'datetime', render: (v) => formatTime(v) },
    { key: 'camera_id', label: 'Camera', type: 'camera' },
    { key: 'confidence', label: 'Best read', type: 'confidence', render: (v) => v ? `${(v * 100).toFixed(0)}%` : '—' },
    { key: 'plates', label: 'Plate reads (raw)', detailOnly: true, wide: true,
      render: (v) => (Array.isArray(v) && v.length
        ? v.map((p: any) => `${p.plate || p.raw}${p.raw && p.raw !== p.plate ? ` (read “${p.raw}”)` : ''}${p.conf != null ? ` ${(Number(p.conf) * 100).toFixed(0)}%` : ''}`).join(' · ')
        : '—') },
    { key: 'text_count', label: 'Text lines', detailOnly: true, render: (v) => v != null ? String(v) : '—' },
    { key: 'ocr_ms', label: 'OCR time', detailOnly: true, render: (v) => v != null ? `${Number(v).toFixed(0)} ms` : '—' },
  ],
  // Every event no report above covers (panel OCR, belt movement, worker restarts, newer
  // models). Defined once in the shared package: generic columns over the event's metadata.
  ai_analytics: AI_ANALYTICS_COLUMNS as ColumnDef[],
};

function formatTime(epoch: number | null) {
  if (!epoch) return '—';
  const d = new Date(epoch * 1000);
  return d.toLocaleDateString([], { day: '2-digit', month: 'short' }) + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

/* ─── Auth Image Component ─── */
/**
 * THE TABLE'S EVENT FRAMES.
 *
 * They were 44px squares, which was the wrong shape twice over: a square crop of a 16:9 frame
 * throws the sides of the scene away — on a wide shot that is most of what the camera saw — and
 * at 44px what survived was too small to recognise anyway, so the reader opened the preview on
 * every row to answer the question the thumbnail was there to answer.
 *
 * BIG is the ALERT list's frame, to the pixel and at every breakpoint (AlertListTable's
 * AlertThumb, in the shared package). An operator moves between the two lists all day and both
 * ask the same thing of a picture; a snapshot that is one size here and another size there is a
 * difference that means nothing and has to be re-learned on each arrival. Change one and change
 * the other.
 *
 * SMALL is a contact sheet, for the frames past the lead one when an event carries several. It
 * is too small to read on purpose — and grows on hover to roughly the lead's size, which is the
 * cheapest way to look through six crops without leaving the row. The growth is bounded by what
 * the row can hold: the table scrolls, so its box clips, and a thumbnail that scaled past the
 * row's height would be cut off at exactly the moment it was being looked at. The lead frame
 * doesn't grow for the same reason — it used to scale 150% on hover, which was fine at 44px and
 * is a clipped mess at 160; a click opens it full-size instead.
 */
const FRAME_BIG =
  'aspect-video w-28 shrink-0 rounded-lg border border-border bg-muted sm:w-32 lg:w-40';
const FRAME_SMALL =
  'relative aspect-video w-12 shrink-0 rounded-md border border-border bg-muted transition-transform duration-150 hover:z-20 hover:scale-[1.9] hover:ring-2 hover:ring-brand/40 lg:w-14';
/** The remainder, when even the contact sheet would run long. Not a frame: it costs no tunnel
 *  round trip and opens nothing — the record behind the row carries every frame in a carousel. */
const FRAME_MORE =
  'flex aspect-video w-12 items-center justify-center rounded-md border border-dashed border-border text-[10px] font-semibold tabular-nums text-muted-foreground lg:w-14';

/**
 * How many frames may be full size before the cell switches to lead-plus-contact-sheet, and how
 * many thumbnails that sheet holds.
 *
 * ONE. An event with a second frame has a second frame because the model cropped something out
 * of the first — so the two are not peers to be shown side by side at equal weight, they are a
 * scene and a detail of it. Giving them equal billing made the row read as two separate events
 * and cost the width of a whole column to say it. The lead is the scene; the crops are a contact
 * sheet beside it, at a glance apiece, big on hover.
 *
 * The sheet stops at three because four pictures is the most a row can carry without the images
 * column being wider than everything else on it — and on the cloud each one of them is its own
 * trip through the node tunnel, so the ceiling is a request budget as well as a layout. The
 * remainder becomes a `+N`, which costs neither.
 */
const FRAMES_BIG = 1;
const FRAMES_SMALL = 3;

function AuthImage({ nodeId, model, eventId, imageType, onClick, small = false }: {
  nodeId: string; model: string; eventId: string; imageType: string; onClick?: () => void;
  /** Contact-sheet size, for the frames behind the lead one. */
  small?: boolean;
}) {
  const [src, setSrc] = useState<string | null>(null);
  const revokeRef = useRef<string | null>(null);

  useEffect(() => {
    if (!nodeId || !model || !eventId || !imageType) return;
    let cancelled = false;
    getNodeModelEventImage(nodeId, model, eventId, imageType)
      .then((blob: Blob) => {
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        revokeRef.current = url;
        setSrc(url);
      })
      .catch(() => { });
    return () => {
      cancelled = true;
      if (revokeRef.current) URL.revokeObjectURL(revokeRef.current);
    };
  }, [nodeId, eventId, imageType]);

  // The placeholder is the same box, so a row doesn't reflow when its frame lands — and on the
  // cloud every frame is a tunnel round trip, so it is a box that is on screen for a while.
  const frame = small ? FRAME_SMALL : FRAME_BIG;
  if (!src) return <div className={`${frame} animate-pulse`} />;
  return (
    <img
      src={src}
      alt={imageType}
      title={imageType}
      onClick={onClick}
      className={`${frame} cursor-pointer object-cover`}
    />
  );
}

/**
 * Every snapshot of one event: the frames down the left, the chosen one large on the right.
 *
 * The cloud can't load a node's image by URL, so each frame is a separate base64 round trip
 * through the tunnel. They're fetched in display order (wide shot first) and awaited one at a
 * time rather than in parallel: the first frame is the one the reader is waiting for, and a
 * burst of concurrent tunnel calls delays it behind the crops nobody has asked for yet.
 *
 * Each frame is HANDED OVER AS IT ARRIVES. It used to collect every image and publish the lot
 * at the end, which on a multi-image event meant the record sat on a grey placeholder for the
 * length of three or four tunnel round trips — and if one frame hung or 404'd, the wide shot
 * that had already arrived was never shown at all. Now the first frame appears as soon as it
 * lands and the picker fills in behind it.
 */
function EventSnapshotGallery({ nodeId, model, eventId, imageTypes, onImageClick, arrowKeys, frameClassName, focusImageKey, selectToken, overlayFor, onSelectionChange }: {
  nodeId: string; model: string; eventId: string; imageTypes: string[];
  onImageClick?: (imageType: string) => void;
  /** Passed straight through — the frame now on screen. See SnapshotGallery. */
  onSelectionChange?: (image: GalleryImage, index: number) => void;
  /** Passed straight through — what the frame on screen is about. See SnapshotGallery. */
  overlayFor?: (image: GalleryImage, index: number) => ReactNode;
  /** Passed through: `updown` leaves ← → to the record pager stepping through events. */
  arrowKeys?: 'updown' | 'both' | 'off';
  frameClassName?: string;
  /**
   * The frame this record is about, by image type — see EventDetailBody's note.
   *
   * It matters more here than on a node. Frames arrive one at a time through the tunnel, so the
   * gallery renders with only the wide shot and the person's crop lands seconds later; the
   * gallery therefore selects it on ARRIVAL rather than on mount.
   */
  focusImageKey?: string;
  /** Bumped by the caller to re-select `focusImageKey` — see SnapshotGallery's `selectToken`. */
  selectToken?: number;
}) {
  /** `type` is carried alongside so a click maps to the RIGHT image type even when an earlier
   *  frame failed to arrive — indexing into `imageTypes` would then be off by one. */
  const [images, setImages] = useState<(GalleryImage & { type: string })[]>([]);
  const [loading, setLoading] = useState(true);
  const typeKey = imageTypes.join(',');

  useEffect(() => {
    if (!nodeId || !model || !eventId || imageTypes.length === 0) {
      setImages([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    const made: string[] = [];
    setImages([]);
    setLoading(true);

    /**
     * THE FRAME THE RECORD IS ABOUT GOES FIRST.
     *
     * `imageTypes` is in READING order — wide shot, then the crops out of it — and fetching in
     * that order meant a Search result opened on a grey panel until the whole frame had crossed
     * the tunnel, even though the crop it was opened on is the one the RESULT CARD already
     * streamed and the browser is still holding. The reader watched the same picture load a
     * second time, more slowly, having already seen it.
     *
     * Asking for that one first makes the record open on it immediately (the image cache in
     * api.service answers without a request), and the wide shot arrives behind it. Nothing about
     * the DISPLAY order changes — see the sorted insert below.
     */
    const rank = new Map(imageTypes.map((t, i) => [t, i]));
    const fetchOrder = focusImageKey && imageTypes.includes(focusImageKey)
      ? [focusImageKey, ...imageTypes.filter((t) => t !== focusImageKey)]
      : imageTypes;

    (async () => {
      for (const type of fetchOrder) {
        try {
          const blob: any = await getNodeModelEventImage(nodeId, model, eventId, type);
          if (cancelled) break;
          // A tunnel 404/timeout still resolves as a (JSON/text) Blob — only keep real images.
          if (blob && (!blob.type || blob.type.startsWith('image/'))) {
            const url = URL.createObjectURL(blob);
            made.push(url);
            // Published one at a time: the reader gets the first frame while the rest are still
            // in flight. `type` travels with it so a frame that never arrives cannot shift the
            // click-through mapping of the ones that did.
            // `key` is what SnapshotGallery matches `selectKey` against; `type` is what a click
            // maps back to. The same string, named for each of its two readers.
            //
            // INSERTED IN READING ORDER, not arrival order: the picker must list the wide shot
            // above the crops taken out of it however the tunnel happened to deliver them. The
            // gallery files each frame's state under its own key, so a late arrival sorting into
            // the middle costs nothing.
            setImages((prev) => [...prev, { url, label: snapshotTypeLabel(type), type, key: type }]
              .sort((a, b) => (rank.get(a.type) ?? 0) - (rank.get(b.type) ?? 0)));
            setLoading(false);
          }
        } catch { /* skip this image */ }
      }
      if (cancelled) { made.forEach((u) => URL.revokeObjectURL(u)); return; }
      setLoading(false);
    })();
    return () => { cancelled = true; made.forEach((u) => URL.revokeObjectURL(u)); };
    /**
     * `focusImageKey` is READ by this effect and deliberately NOT a dependency.
     *
     * It changes when the reader steps ← → between two people of the SAME event — the record is
     * the same record, only the subject differs — and listing it here would tear the whole
     * gallery down and stream every frame again for a change of selection. It only ever decides
     * which frame is asked for FIRST, which is a question with an answer at the moment the
     * fetching starts; the value captured then is the right one.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeId, model, eventId, typeKey]);

  if (loading) return <div className={`${frameClassName || 'aspect-video'} w-full animate-pulse rounded-lg border border-border bg-muted`} />;
  if (images.length === 0) {
    return (
      <div className="rounded-xl border border-border bg-muted px-4 py-6 text-center text-[12px] text-muted-foreground">
        Snapshots unavailable (node unreachable).
      </div>
    );
  }
  return (
    <SnapshotGallery
      /* Re-armed per record, so stepping through results with ← → opens each one on ITS person
         rather than keeping the frame chosen for the previous record. */
      key={eventId}
      images={images}
      selectKey={focusImageKey}
      selectToken={selectToken}
      arrowKeys={arrowKeys}
      frameClassName={frameClassName}
      onImageClick={onImageClick ? (_img, i) => onImageClick(images[i]?.type) : undefined}
      overlayFor={overlayFor}
      onSelectionChange={onSelectionChange}
    />
  );
}

/* ─── Full-size preview image ─── */
function AuthPreviewImage({ nodeId, model, eventId, imageType }: { nodeId: string; model: string; eventId: string; imageType: string }) {
  const [src, setSrc] = useState<string | null>(null);
  const revokeRef = useRef<string | null>(null);

  useEffect(() => {
    if (!nodeId || !model || !eventId || !imageType) return;
    let cancelled = false;
    getNodeModelEventImage(nodeId, model, eventId, imageType)
      .then((blob: Blob) => {
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        revokeRef.current = url;
        setSrc(url);
      })
      .catch(() => { });
    return () => {
      cancelled = true;
      if (revokeRef.current) URL.revokeObjectURL(revokeRef.current);
    };
  }, [nodeId, eventId, imageType]);

  if (!src) return <div className="w-full h-48 bg-muted rounded border border-border animate-pulse" />;
  return <img src={src} alt={imageType} className="max-w-full max-h-[80vh] rounded-xl shadow-2xl" />;
}

/** The per-model columns, safe for a model this build doesn't know about. */
export function eventColumns(model?: string | null): ColumnDef[] {
  return (model && MODEL_COLUMNS[model]) || MODEL_COLUMNS.person;
}

/**
 * The title of one event's record: "Event Details" plus what the model actually called it,
 * highlighted — the model's name and the event type are what tell an operator which of eight
 * detectors they're reading, and in a full-width panel that has to be legible at a glance.
 */
export function EventDetailTitle({ event, model }: { event: any; model?: string | null }) {
  const modelLabel = reportModelLabel(model || undefined);
  const type = eventTypeLabel(event);
  // The type only when it SAYS something: on most models it is the model's own name again,
  // which printed "Person Detection · Person Detection".
  const showType = type && type.toLowerCase() !== String(modelLabel || '').toLowerCase();
  return (
    <span className="flex flex-wrap items-center gap-2">
      <span>Event Details</span>
      {/* `bg-brand-light`, not `bg-brand/15` — the opacity modifier compiles to nothing on the
          theme's var() tokens, so the chip had no background at all. */}
      <span className="rounded-full border border-brand bg-brand-light px-2.5 py-0.5 text-[12px] font-semibold text-brand">
        {modelLabel}
      </span>
      {showType && (
        <span className="rounded-full border border-border bg-muted px-2.5 py-0.5 text-[12px] font-medium text-foreground">
          {type}
        </span>
      )}
    </span>
  );
}

/**
 * EventDetailBody — the snapshots and facts of ONE event, shared by the modal the report table
 * opens and the routed /events/:model/:id page, so the two can't drift into different records.
 *
 * The cloud can't load a node's snapshot by URL, so the media slot is the tunnel-streaming
 * carousel; the local app's copy of this page hands the same layout plain URLs. That difference
 * is the whole reason the shared layout takes the media as a slot.
 */
export function EventDetailBody({
  event,
  model,
  nodeId,
  cameraNameMap = {},
  personName,
  onImageClick,
  actions = null,
  hint = null,
  extraFields,
  focusImageKey,
  focusSubjectSeq,
  headerActions = null,
  onClose,
}: {
  event: any;
  model: string;
  nodeId: string;
  /** In a modal: the pager and "Full page", drawn in the record's own header. */
  headerActions?: ReactNode;
  /** In a modal: the record's close button. */
  onClose?: () => void;
  cameraNameMap?: Record<string, string>;
  personName?: (event: any) => string | null | undefined;
  onImageClick?: (imageType: string) => void;
  actions?: ReactNode;
  hint?: ReactNode;
  /**
   * Extra record fields the CALLER knows and this component cannot derive.
   *
   * The Search modal's subject attributes arrive this way. They are on the projection row the
   * search returned, not on the typed-table record fetched here, and the normalisation behind
   * them (the per-frame union, the vocabulary check) is the server's — re-deriving it from raw
   * `metadata` in this file would let the record disagree with the card it was opened from.
   *
   * Appended AFTER the model's own fields, so a record always reads the same way down to the
   * point where the caller's additions start.
   */
  extraFields?: EventFact[];
  /**
   * Which subject of the frame this record was opened from, by `subject_seq`.
   *
   * Only marks a row in the breakdown — the list is never re-ordered around it. See
   * SubjectBreakdown on why: the order is the module's, and it is the order the snapshot strip
   * and the crop numbering already use.
   */
  focusSubjectSeq?: number | null;
  /**
   * Which frame this record is ABOUT, as an `image_urls` key — `personCrop2`, say.
   *
   * An event is a frame and may hold seven people; a Search result is ONE of them. Opening
   * their record on the wide shot hands the reader back the crowd they were just told apart
   * from, and makes them find the person again in it. The Search page passes the projection
   * row's `crop_key`, which is the same frame its card showed.
   *
   * Optional and absent everywhere else on purpose: the reports table and the routed
   * /events/:model/:id page open on a record ABOUT the whole event, where the wide shot is the
   * right first frame and always was.
   */
  focusImageKey?: string;
}) {
  const cameraName = event?.camera_id ? cameraNameMap[event.camera_id] || event.camera_id : undefined;
  // "Clip ±10s" and "Go live" — the same footage and live view the alert record offers.
  const { playback, renderLive } = useCameraMedia(nodeId, event?.camera_id, cameraName);

  /**
   * Which frame the viewer is showing, and a counter so selecting the SAME one again counts.
   *
   * Seeded from `focusImageKey` (the subject the record was opened from) and re-seeded when that
   * changes, which is what stepping ← → between two people of one frame does. The counter is
   * what lets a reader click a subject in the breakdown, look at the full frame, and then click
   * that subject again — see SnapshotGallery's `selectToken`.
   */
  const [picked, setPicked] = useState<{ key?: string; n: number }>({ key: focusImageKey, n: 0 });

  /**
   * The frame actually on screen — see the local copy. Reported by the gallery, because ↑ ↓ and
   * the picker move the selection without the caller being told otherwise.
   */
  const [shownKey, setShownKey] = useState<string | null>(null);
  useEffect(() => { setShownKey(null); }, [event?.id]);
  const shownSubject: EventSubject | null = (event?.subjects || []).find(
    (s: EventSubject) => s.crop_key && s.crop_key === shownKey,
  ) || null;
  useEffect(() => {
    setPicked({ key: focusImageKey, n: 0 });
  }, [focusImageKey, event?.id]);

  const imageTypes = useMemo(() => {
    const parse = (v: any) => {
      if (typeof v === 'string') { try { v = JSON.parse(v); } catch { return null; } }
      return v && typeof v === 'object' && Object.keys(v).length ? v : null;
    };
    const imgs = parse(event?.image_urls) || parse(event?.image_paths);
    if (!imgs) return [] as string[];
    return Object.keys(imgs).filter((k) => imgs[k]).sort((a, b) => snapshotTypeRank(a) - snapshotTypeRank(b));
  }, [event?.image_urls, event?.image_paths]);

  const { facts, fields } = splitEventFacts(event, eventColumns(model), {
    cameraName: (id: string) => cameraNameMap[id],
    personName,
  });
  /* The caller's per-subject attributes belong in the APPEARANCE block, not among the event's
     fields — see the local copy and EventDetail's `subjectPanel`. */
  const openedAppearance = extraFields?.length ? extraFields : null;
  // Not a column, but it is what a support ticket quotes. LAST, after everything describing the
  // event itself — it is a handle for a ticket, not a finding.
  if (event?.id) fields.push({ key: '__id', label: 'Event ID', value: String(event.id), mono: true });

  return (
    <EventDetail
      event={event}
      model={model}
      cameraName={cameraName}
      playback={playback}
      renderLive={renderLive}
      headerActions={headerActions}
      onClose={onClose}
      media={(slot) => (
        imageTypes.length > 0 ? (
          <EventSnapshotGallery
            nodeId={nodeId}
            model={model}
            eventId={event.id}
            imageTypes={imageTypes}
            focusImageKey={picked.key}
            selectToken={picked.n}
            onImageClick={onImageClick}
            /* Who each frame is, in the picker that selects it — see the local copy. The scene
               gets no detail; subjects with no crop are listed by `extra`. */
            onSelectionChange={(img: GalleryImage) => { setShownKey(img?.key ?? null); slot.onSelectionChange(img); }}
            overlayFor={(img: GalleryImage) => {
              const subjects: EventSubject[] = event?.subjects || [];
              const one = subjects.find((x: EventSubject) => x.crop_key && x.crop_key === img.key);
              if (!one) return null;
              return (
                <SubjectOverlay
                  subject={one}
                  marked={focusSubjectSeq != null && one.subject_seq === focusSubjectSeq}
                />
              );
            }}
            // One axis each: ← → stay with the record pager stepping through EVENTS, ↑ ↓ move
            // between this event's frames. No focus arbitration, so the same key always does
            // the same thing.
            arrowKeys="updown"
            frameClassName={slot.frameClassName}
          />
        ) : (
          <div className={`flex items-center justify-center rounded-xl border border-border bg-muted text-[14px] text-muted-foreground ${slot.frameClassName}`}>
            No snapshots for this event
          </div>
        )
      )}
      hint={hint}
      facts={facts}
      fields={fields}
      /* The subjects the picker cannot carry — the ones with no crop of their own. Everybody
         else is already described beside their own face in the rail, and saying it twice is what
         this list looked like on the cloud: eight people in the picker and the same eight again
         underneath, each with the same two colours.

         FILTERED ON WHAT THE EVENT DECLARES (`imageTypes`), not on what has streamed — which is
         what it used to be, and is why the two nodes disagreed. A crop's existence is a fact of
         the record and is known on the first render; the tunnel's delivery schedule is not. Under
         the old test this block opened holding EVERY subject and emptied as the frames landed —
         a list that appears and then vanishes — and any crop that 404'd left its subject
         duplicated at the bottom for good. */
      extra={(
        <SubjectBreakdown
          subjects={(event?.subjects || []).filter(
            (s: EventSubject) => !(s.crop_key && imageTypes.includes(s.crop_key)),
          )}
          highlightSeq={focusSubjectSeq}
        />
      )}
      /* Whose appearance, tied to the frame on screen — see the local copy. The block is the
         SHARED `AppearancePanel`: it was written out in full here and again there, which is two
         chances to describe one person two ways. */
      subjectPanel={(
        <AppearancePanel
          facts={(shownSubject ? subjectAttributeFacts(shownSubject) : openedAppearance) || []}
          subjectSeq={shownSubject?.subject_seq}
          subjectLabel={shownSubject?.subject_label}
          ofFrame={Boolean(shownSubject)}
        />
      )}
      actions={actions}
      note={reportModelNote(model)}
    />
  );
}

/* ═══════════════════════════════════════════════════════════════
   Alert Investigation Page (Cloud Node)
   ═══════════════════════════════════════════════════════════════ */

/**
 * An occupancy-series failure, as a sentence rather than a stack trace.
 *
 * The status is what tells the operator which of the moving parts is behind: this endpoint is
 * newer than the services around it, so a 404 is nearly always a node still running the previous
 * build rather than a wrong URL.
 */
function occupancyFailure(err: any): string {
  const status = err?.response?.status;
  const said = err?.response?.data?.message;
  // On the cloud the answer crosses THREE deployments — this app, the cloud backend, and the
  // site's own service — and a 404 can come from either of the last two. Naming both beats
  // naming the wrong one.
  if (status === 404) return 'The occupancy endpoint was not found. Either the cloud backend or this site\'s command-center service is running a build older than this page.';
  if (status === 403) return 'You do not have permission to read AI events on this site.';
  if (status === 503) return said || 'The site is offline, so its occupancy readings could not be fetched.';
  if (status === 504) return 'The site did not answer in time. It may be busy or on a slow link.';
  return said || err?.message || 'The request failed.';
}

/**
 * @param lockedModel  When set, the page is a single-model Reports surface: the
 *   AI-Model dropdown and the model tab bar are hidden and the model is pinned.
 *   Omit it for the legacy all-models investigation view.
 */
export default function AlertInvestigationPage({ lockedModel = null }: { lockedModel?: string | null }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedNode } = useNode();

  // ── Use global selected node ──
  const nodeId = selectedNode?.nodeId || '';

  // ── Filters (synced with URL) ──
  const [groupId, setGroupId] = useState(searchParams.get('group_id') || '');
  const [cameraId, setCameraId] = useState(searchParams.get('camera_id') || '');
  const [model, setModel] = useState(lockedModel || searchParams.get('model') || 'person');
  // ── Vehicle watchlist (Vehicle Crossing report only) ──
  // `flaggedOnly` filters the table to crossings the watchlist matched AT INGEST — the
  // is_blacklisted column, not a live join, so de-listing a plate does not erase the
  // crossings it flagged. `seedPlate` carries a plate from a row into the panel's add form.
  const [flaggedOnly, setFlaggedOnly] = useState(searchParams.get('flagged') === '1');
  // The watchlist panel is closed until the header button asks for it. Closed means UNMOUNTED,
  // not merely invisible: the panel loads the list on mount, and an always-mounted copy would
  // put a round trip through the node tunnel on every Vehicle Crossing page load for a panel
  // most visits never open.
  const [showBlacklistPanel, setShowBlacklistPanel] = useState(false);
  const [seedPlate, setSeedPlate] = useState<string | null>(null);
  // Bumped after a watchlist edit so the table refetches: the same plate crossing again
  // should now be flagged (or no longer flagged), and a stale table would contradict the list
  // the operator is looking at directly underneath it.
  const [blacklistVersion, setBlacklistVersion] = useState(0);
  const [dateFrom, setDateFrom] = useState(searchParams.get('date_from') || '');
  const [dateTo, setDateTo] = useState(searchParams.get('date_to') || '');
  const [search, setSearch] = useState(searchParams.get('search') || '');
  // Debounced copy of `search` — the fetch keys off this so typing doesn't fire a
  // request per keystroke; the input itself stays on `search` (instant echo).
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  /**
   * `?event_id=…` — a deep link to ONE event. The attendance timeline links here so an
   * operator can go from a sighting straight to its recognition record.
   *
   * Held as a filter rather than as a modal-only concern: the LIST narrows to that event
   * too, so dismissing the detail doesn't drop the reader into a page of unrelated rows
   * they never asked for. Cleared by the chip in the filter bar.
   */
  const [focusEventId, setFocusEventId] = useState(searchParams.get('event_id') || '');

  // ── Data ──
  const [events, setEvents] = useState<any[]>([]);
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1', 10));
  const [totalPages, setTotalPages] = useState(1);
  // Server-side paging: the page size is part of the request, so changing it refetches.
  const [pageSize, setPageSize] = useState(EVENTS_PAGE_SIZE_OPTIONS[1]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // `/reports/:model` is one route, so navigating between report pages changes the
  // param without remounting — re-pin the model and reset paging when it does.
  useEffect(() => {
    if (lockedModel && lockedModel !== model) {
      setModel(lockedModel);
      setPage(1);
    }
  }, [lockedModel]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Lookup data ──
  const [groups, setGroups] = useState<any[]>([]);
  const [cameras, setCameras] = useState<any[]>([]);
  const [cameraNameMap, setCameraNameMap] = useState<Record<string, string>>({});

  // ── Image preview ──
  const [previewEvent, setPreviewEvent] = useState<{ eventId: string; imageType: string } | null>(null);

  /**
   * WHICH INSTRUMENT THIS MODEL'S RESULTS ARE DRAWN WITH.
   *
   * Cards, rows, or — for one model — a chart. Cards and Table are two presentations of the SAME
   * page of events (see EventListCards): an AI event is a claim about something a camera saw, and
   * the picture settles it where a row of columns describes it. The choice is remembered per
   * browser under its own key, so an operator can work alerts as a table and reports as cards.
   *
   * `chart` exists for ONE model. `person_count` is not an event detector — it is a timer that
   * flushes an occupancy reading about once a minute per camera, and the snapshot it attaches is
   * simply the frame current at the flush instant. So the event log showed rows reading "3 people"
   * beside a photograph of an empty corridor: the number describes the minute, the picture
   * describes its last second, and the picture is the one a reader believes. A headcount sampled
   * once a minute is a LEVEL, and the instrument for a level is a time series.
   *
   * THE STATE HERE IS THE OPERATOR'S CHOICE, AND IT STARTS EMPTY. Held separately from what is
   * drawn so that "I picked Cards" and "I haven't picked anything, use this model's default" stay
   * different facts — collapsing them is what made an earlier version fold Cards back into the
   * chart the instant it was clicked, since a default expressed as `cards → chart` cannot tell an
   * unset preference from a deliberate one. `null` means unset; `effectiveView` resolves it.
   *
   * The choice is dropped whenever the MODEL changes, because the right default is the new
   * model's, not the previous one's. Only `cards`/`table` are remembered across sessions: `chart`
   * is Person Count's default rather than a preference, and persisting it would have it arrive as
   * a stale choice on a model that cannot draw one.
   */
  const [viewChoice, setViewChoice] = useState<'cards' | 'table' | 'chart' | null>(null);
  const setView = useCallback((v: 'cards' | 'table' | 'chart') => {
    setViewChoice(v);
    if (v === 'cards' || v === 'table') localStorage.setItem('reports.view', v);
  }, []);
  // A new model gets its own default back.
  useEffect(() => { setViewChoice(null); }, [model]);

  /**
   * Can this model be drawn as a series? Only `person_count`.
   *
   * `effectiveView` is what actually renders: the operator's choice where they made one, this
   * model's default where they didn't, and never `chart` on a model that has none.
   */
  const chartable = model === 'person_count';
  const savedView = typeof localStorage === 'undefined' ? null : localStorage.getItem('reports.view');
  const defaultView = chartable ? 'chart' : savedView === 'table' ? 'table' : 'cards';
  const picked = viewChoice || defaultView;
  const effectiveView = picked === 'chart' && !chartable ? 'cards' : picked;


  // ── Event detail view ──
  // The row's index, not the row: the detail modal steps through the page's events with the
  // arrow keys (RecordPager binds them) and Escape closes it (the Modal shell binds that).
  const [selectedEventIndex, setSelectedEventIndex] = useState<number | null>(null);
  const selectedEvent = selectedEventIndex !== null ? events[selectedEventIndex] : null;

  // ── Face / Person Registration ──
  const [registerModal, setRegisterModal] = useState<any | null>(null);
  const [registerName, setRegisterName] = useState('');
  const [registerLabel, setRegisterLabel] = useState('visitor');
  const [registerNotes, setRegisterNotes] = useState('');
  const [registerEmployeeCode, setRegisterEmployeeCode] = useState('');
  // Employment is a CLASSIFICATION of a registered person, not a kind of registration — so it
  // is a checkbox alongside the person's details rather than a value in the Label list.
  const [registerAsEmployee, setRegisterAsEmployee] = useState(false);
  /**
   * The watchlist entry to create alongside the registration, or null for none.
   *
   * Null by default: most registrations are ordinary people, and a form that opened with a
   * category already chosen would invite the operator to skim past it. See
   * PersonWatchlistToggle for why this is a real entry rather than a `label` of "blacklist".
   */
  const [registerWatchlist, setRegisterWatchlist] = useState<PersonWatchlistValue | null>(null);
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registerError, setRegisterError] = useState('');
  const [linkFilter, setLinkFilter] = useState('');

  // ── Registered Persons Panel ──
  const [showRegisteredPanel, setShowRegisteredPanel] = useState(false);
  const [registeredFaces, setRegisteredFaces] = useState<any[]>([]);
  const [registeredLoading, setRegisteredLoading] = useState(false);
  /**
   * What the drawer is asking the node for. The registry is searched and filtered SERVER-side
   * — see RegisteredPeopleToolbar — so `registeredFaces` is one filtered page of the registry
   * and no longer stands for "everyone registered". Anything that needs everyone reads the
   * roster below instead.
   */
  const [registryQuery, setRegistryQuery] = useState('');
  const [registryFilter, setRegistryFilter] = useState<PeopleRegistryFilter>('all');
  // Which KIND of watchlist entry — blacklisted, wanted, missing — or '' for all of them.
  // Separate from the filter above because it narrows that filter rather than replacing it,
  // and it is cleared whenever the drawer leaves the watchlist so a stale category cannot
  // come back and silently hide most of the registry on the operator's next visit.
  const [registryCategory, setRegistryCategory] = useState('');
  const [registryPage, setRegistryPage] = useState(1);
  const [registryTotal, setRegistryTotal] = useState(0);
  // Typing re-queries the node, so it waits for a pause rather than firing per keystroke.
  const [registryQueryDebounced, setRegistryQueryDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setRegistryQueryDebounced(registryQuery.trim()), 300);
    return () => clearTimeout(t);
  }, [registryQuery]);
  const [expandedPersonId, setExpandedPersonId] = useState<string | null>(null);
  const [personFaces, setPersonFaces] = useState<any[]>([]);
  const [personFacesLoading, setPersonFacesLoading] = useState(false);

  const { hasPermission } = useAuth();
  const canEdit = hasPermission('alerts.edit'); // gates registered-face manage (delete + unlink)
  // The watchlist is an AI-events registry, not an alert, so it is gated by the same key the
  // persons registry uses rather than by `alerts.edit` — a role that may acknowledge alerts is
  // not thereby a role that may make every gate on the node alert on a plate. Matches the
  // cloud proxy's own gate on /analytics/vehicle-blacklist.
  const canManageWatchlist = hasPermission('ai_events.register');

  // ── Load groups + cameras when node changes ──
  useEffect(() => {
    if (!nodeId) { setGroups([]); setCameras([]); setCameraNameMap({}); return; }
    getNodeGroups(nodeId).then((res: any) => {
      setGroups(res?.data || res || []);
    }).catch(() => setGroups([]));
    getNodeCameras(nodeId).then((res: any) => {
      const list = res?.cameras || res || [];
      setCameras(list);
      const map: Record<string, string> = {};
      list.forEach((c: any) => { map[c.id] = c.name || c.id; });
      setCameraNameMap(map);
    }).catch(() => { setCameras([]); setCameraNameMap({}); });
  }, [nodeId]);

  /**
   * ── Everyone the page has ever seen registered, by person_id ──
   *
   * The Identity column and the "link to an existing person" picker both need the REGISTRY, not
   * whatever slice the drawer is showing. Once the drawer gained a filter, reading those off
   * `registeredFaces` would have meant that filtering the drawer to the watchlist renamed every
   * other person in the table back to "Unknown", and that the link picker offered only flagged
   * people to link a new face to.
   *
   * So rows accumulate here and are never removed by a filter: every fetch merges what it saw.
   * A rename still lands, because the merge overwrites by person_id.
   */
  const [personRoster, setPersonRoster] = useState<Record<string, any>>({});
  const mergeRoster = useCallback((rows: any[]) => {
    if (!Array.isArray(rows) || rows.length === 0) return;
    setPersonRoster((prev) => {
      const next = { ...prev };
      rows.forEach((r: any) => { if (r?.person_id) next[r.person_id] = r; });
      return next;
    });
  }, []);

  // ── person_id → name map for resolving the Identity column ──
  const personNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    Object.values(personRoster).forEach((f: any) => {
      if (f.person_id) map[f.person_id] = f.name || f.label || f.person_id;
    });
    return map;
  }, [personRoster]);

  /** The roster as a list, for the link picker — by name, so it reads like a directory. */
  const rosterList = useMemo(
    () => Object.values(personRoster).sort((a: any, b: any) => String(a.name || '').localeCompare(String(b.name || ''))),
    [personRoster],
  );

  const resolvePersonName = (event: any): string | null =>
    event?.matched_person_name || personNameMap[event?.person_id] || personNameMap[event?.matched_person_id] || null;

  // ── Resolve the selected group → its camera ids ──
  // The node's `/groups` returns bare group rows (no nested cameras), so membership has to
  // be fetched. `recursive` includes the cameras of every sub-group, which is what a user
  // picking a parent group expects. `null` = not resolved yet (don't filter on a stale set).
  const [groupCameraIds, setGroupCameraIds] = useState<string[] | null>(null);
  useEffect(() => {
    if (!nodeId || !groupId) { setGroupCameraIds(null); return; }
    let cancelled = false;
    setGroupCameraIds(null);
    getGroupCameras(nodeId, groupId, true)
      .then((res: any) => {
        if (cancelled) return;
        const list = Array.isArray(res) ? res : (res?.data || []);
        setGroupCameraIds(list.map((c: any) => c.id));
      })
      .catch(() => { if (!cancelled) setGroupCameraIds([]); });
    return () => { cancelled = true; };
  }, [nodeId, groupId]);

  // ── Filter the camera dropdown by the selected group ──
  const filteredCameras = useMemo(() => {
    if (!groupId || !groupCameraIds) return cameras;
    const ids = new Set(groupCameraIds);
    return cameras.filter((c: any) => ids.has(c.id));
  }, [cameras, groupId, groupCameraIds]);

  // ── Reset filters when node changes (skip first mount if URL has params) ──
  const isFirstMount = useRef(true);
  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }
    // `?event_id=` goes with them: it names one event in the site just left, and the focused
    // fetch ignores the filter bar — so it would hold the page on a record this site can't
    // return, with controls that appear to do nothing.
    setGroupId(''); setCameraId(''); setPage(1); setFocusEventId('');
  }, [nodeId]);

  // ── Sync filters → URL ──
  useEffect(() => {
    const params: Record<string, string> = {};
    // On a Reports page the model is in the path, so keep it out of the query.
    if (model && !lockedModel) params.model = model;
    if (groupId) params.group_id = groupId;
    if (cameraId) params.camera_id = cameraId;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (search) params.search = search;
    if (flaggedOnly) params.flagged = '1';
    if (focusEventId) params.event_id = focusEventId;
    if (page > 1) params.page = String(page);
    setSearchParams(params, { replace: true });
  }, [model, groupId, cameraId, dateFrom, dateTo, search, flaggedOnly, focusEventId, page, setSearchParams]);

  // Any filter edit drops the single-event focus. The focused fetch ignores the filter bar,
  // so leaving the two live together would give the reader controls that appear to do nothing.
  const filterSig = `${model}|${groupId}|${cameraId}|${dateFrom}|${dateTo}|${debouncedSearch}|${flaggedOnly}`;
  const initialFilterSig = useRef(filterSig);
  useEffect(() => {
    if (filterSig !== initialFilterSig.current) setFocusEventId('');
  }, [filterSig]);

  // Cameras whose NAME matches the search term — resolved here because camera names
  // aren't in the command-center DB. Sent as `search_camera_ids` so the DB search's
  // OR-group can also hit "camera name" matches.
  const searchCameraIds = useMemo(() => {
    const term = debouncedSearch.trim().toLowerCase();
    if (!term) return '';
    return cameras
      .filter((c: any) => (c.name || '').toLowerCase().includes(term))
      .slice(0, 100)
      .map((c: any) => c.id)
      .join(',');
  }, [debouncedSearch, cameras]);

  // ── Fetch model events ──
  // Guards against out-of-order responses over the tunnel: a slow older request must
  // not clobber the results of a newer filter change that already resolved.
  const fetchSeq = useRef(0);
  const fetchEvents = useCallback(async () => {
    if (!nodeId) { setEvents([]); setTotal(0); setTotalPages(1); return; }
    const seq = ++fetchSeq.current;

    // Deep-linked to one event: fetch that row by id instead of running the filter query.
    // The node resolves either the AI `event_id` or the DB id, which matters because the
    // attendance timeline only carries the former.
    if (focusEventId) {
      setLoading(true);
      try {
        const row = await getNodeModelEvent(nodeId, model, focusEventId);
        if (seq !== fetchSeq.current) return;
        const rows = row ? [row] : [];
        setEvents(rows);
        setTotal(rows.length);
        setTotalPages(1);
        // Open the record itself. Arriving on a one-row list the reader still has to click
        // would be a worse landing than the sighting they clicked from.
        setSelectedEventIndex(rows.length ? 0 : null);
      } catch {
        if (seq !== fetchSeq.current) return;
        // 404 = deleted or aged out of retention; also how an offline node reads. The empty
        // state names the first, which is by far the likelier of the two here.
        setEvents([]);
        setTotal(0);
        setTotalPages(1);
        setSelectedEventIndex(null);
      } finally {
        if (seq === fetchSeq.current) setLoading(false);
      }
      return;
    }

    // A group is selected but its cameras haven't come back yet — hold the spinner rather
    // than firing an unscoped query whose results would flash the wrong rows.
    if (groupId && groupCameraIds === null) { setLoading(true); return; }
    const groupCsv = groupId && groupCameraIds ? groupCameraIds.join(',') : '';
    // Group with no cameras → no events, and no point asking the node.
    if (groupId && !cameraId && !groupCsv) {
      setEvents([]); setTotal(0); setTotalPages(1); setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const params: Record<string, any> = { page, limit: pageSize, sort_by: 'event_timestamp', sort_order: 'desc' };
      // A specific camera wins; otherwise scope to every camera in the group (the dropdown
      // is already limited to the group, so the two can't disagree).
      if (cameraId) params.camera_id = cameraId;
      else if (groupCsv) params.camera_ids = groupCsv;
        if (debouncedSearch.trim()) {
        params.search = debouncedSearch.trim();
        if (searchCameraIds) params.search_camera_ids = searchCameraIds;
      }
      // Backend expects `from`/`to` (epoch seconds). Both bounds parse in LOCAL time
      // (dateFrom used to parse as UTC midnight while dateTo parsed local — inconsistent).
      if (dateFrom) {
        const t = new Date(dateFrom + 'T00:00:00').getTime();
        if (!Number.isNaN(t)) params.from = String(t / 1000);
      }
      if (dateTo) {
        const t = new Date(dateTo + 'T23:59:59.999').getTime();
        if (!Number.isNaN(t)) params.to = String(t / 1000);
      }
      // Only vehicle_crossing_events has this column. Sending it on another model would be a
      // filter the node quietly ignores, which reads as a broken toggle.
      if (model === 'vehicle_crossing' && flaggedOnly) params.is_blacklisted = 'true';

      const res = await getNodeModelEvents(nodeId, model, params);
      if (seq !== fetchSeq.current) return; // stale response — a newer fetch is in flight
      // Response may be: array directly, or { data: [], pagination: {} }
      const eventList = Array.isArray(res) ? res : (res?.data || []);
      const pagination = Array.isArray(res) ? null : res?.pagination;
      setEvents(eventList);
      setTotal(pagination?.total || eventList.length);
      setTotalPages(pagination?.total_pages || 1);
    } catch (err) {
      if (seq !== fetchSeq.current) return;
      console.error('Failed to fetch model events:', err);
      setEvents([]);
      setTotal(0);
      setTotalPages(1);
    } finally {
      if (seq === fetchSeq.current) setLoading(false);
    }
  }, [nodeId, model, cameraId, groupId, groupCameraIds, debouncedSearch, searchCameraIds, dateFrom, dateTo, page, pageSize, focusEventId, flaggedOnly, blacklistVersion]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  // ── The Person Count chart's series ──
  //
  // A SEPARATE fetch from the event list, on the same filters. The list is paged and ordered;
  // this is an aggregate over the whole window, and the node does the bucketing (see
  // occupancySeriesService) — pulling a day of once-a-minute readings per camera to bucket them
  // in the browser would be tens of thousands of rows to draw a few hundred marks.
  //
  // Only ever runs while the chart is what is on screen, so the other eight reports never pay
  // for it. Same stale-response guard as the list: an operator dragging the date range makes
  // several of these overlap.
  const [occupancy, setOccupancy] = useState<any>(null);
  const [occupancyLoading, setOccupancyLoading] = useState(false);
  /**
   * Why the occupancy request failed, in words an operator can act on — or null when it didn't.
   *
   * Held separately from `occupancy` because "the site reported nothing" and "nobody could be
   * asked" are different facts and must not draw the same empty state. A 404 here almost always
   * means the service is running code older than this page (the endpoint is new); a 503 means the
   * node is unreachable. Both are fixable, and neither is "no data".
   */
  const [occupancyError, setOccupancyError] = useState<string | null>(null);
  const occupancySeq = useRef(0);
  useEffect(() => {
    if (effectiveView !== 'chart' || !chartable || !nodeId) return;
    // A group is selected but its cameras haven't resolved — hold, rather than asking unscoped
    // and flashing every camera on the node into the grid.
    if (groupId && groupCameraIds === null) { setOccupancyLoading(true); return; }

    const seq = ++occupancySeq.current;
    const params: any = {};
    if (cameraId) params.camera_ids = cameraId;
    else if (groupId && groupCameraIds) params.camera_ids = groupCameraIds.join(',');
    // A group with no cameras in it selects nothing — and an empty `camera_ids` would read as
    // "no filter" on the wire, which is the opposite.
    if (groupId && !cameraId && !params.camera_ids) {
      setOccupancy({ from: 0, to: 0, step: 60, buckets: 0, auto_bucket: true, cameras: [], total: { values: [], peak: 0, now: null } });
      setOccupancyError(null);
      setOccupancyLoading(false);
      return;
    }
    // Same local-time parsing as the list, so the chart and the log cover the identical window.
    if (dateFrom) {
      const t = new Date(dateFrom + 'T00:00:00').getTime();
      if (!Number.isNaN(t)) params.from = t / 1000;
    }
    if (dateTo) {
      const t = new Date(dateTo + 'T23:59:59.999').getTime();
      if (!Number.isNaN(t)) params.to = t / 1000;
    }

    setOccupancyLoading(true);
    setOccupancyError(null);
    getNodeOccupancySeries(nodeId, params)
      .then((data: any) => { if (seq === occupancySeq.current) setOccupancy(data); })
      .catch((err) => {
        if (seq !== occupancySeq.current) return;
        console.error('Failed to fetch occupancy series:', err);
        setOccupancy(null);
        setOccupancyError(occupancyFailure(err));
      })
      .finally(() => { if (seq === occupancySeq.current) setOccupancyLoading(false); });
  }, [effectiveView, chartable, nodeId, cameraId, groupId, groupCameraIds, dateFrom, dateTo]);


  // ── Face / person registration helpers ──
  // The event's AI identifier is `event_id` (the persons/employee from-event endpoints look
  // up facial_recognition_events by it); `.id` is only used for image fetches.
  const registerEventId = (ev: any): string => ev?.event_id || ev?.id;

  const openRegisterModal = (event: any) => {
    setRegisterModal(event);
    setRegisterName('');
    setRegisterLabel('visitor');
    setRegisterNotes('');
    setRegisterEmployeeCode('');
    setRegisterAsEmployee(false);
    setRegisterWatchlist(null);
    setRegisterError('');
    setLinkFilter('');
    // Load the persons registry so the "link to existing" list is available. The roster, not
    // the drawer's filtered page: a face can be linked to anybody registered.
    if (rosterList.length === 0) fetchPersonRoster();
  };

  /**
   * Register the face event as a new identity, or (with linkToPersonId) attach it to an
   * existing person. Vectors + status come back asynchronously from the AI module — refresh
   * events/persons to pick up the new state.
   *
   * ONE REGISTRATION PATH. This used to fork on the Label dropdown: choosing "Employee" called
   * a different endpoint and created an employees row there and then, which made employment a
   * property of HOW someone was registered — unchangeable afterwards without deleting them and
   * starting again. It is a property of the PERSON, and people are reclassified.
   *
   * So registration always creates a person. Ticking "employee" adds the employment record in
   * a second call — the same call the Registered People panel makes to promote someone months
   * later, so the two routes into being an employee are the same route.
   *
   * The registration is NOT rolled back if the promote fails: the person is registered and
   * their face enrolled, which is the expensive, irreversible half. The message says what to
   * do instead.
   */
  /**
   * Flag / unflag a registered person.
   *
   * Applies to employees too — an ex-employee is the commonest reason to flag somebody, so the
   * control is on the person and not on whether they are staff.
   */
  /**
   * The person the watchlist dialog is open on, or null.
   *
   * A DIALOG RATHER THAN A PROMPT. `prompt()` could ask for the reason and nothing else, so
   * every person flagged from this page came out critical with alerting on — the node's defaults
   * for a request that says nothing — and the one thing an operator most often wants, a quiet
   * flag on somebody who is watched but not dangerous, was unreachable outside the API.
   */
  const [blacklistTarget, setBlacklistTarget] = useState<any | null>(null);

  const afterWatchlistChange = () => {
    fetchRegisteredFaces();
    if (model === 'face') fetchEvents();
  };

  // ── Register a person from photos, with no face event ──
  // The panel could only ever register somebody a camera had already seen. Enrolling from an
  // HR photo before a first day is just as ordinary, and employees have had it all along.
  // Which person's employment call is in flight — the switch says "Working…" rather than
  // sitting there looking unchanged while the request is out.
  const [employeeBusyId, setEmployeeBusyId] = useState<string | null>(null);
  /**
   * Whether the registration dialog is open. The FIELDS are not here — they belong to
   * `PersonRegisterDialog`, which both dashboards share. A form held in the page's state is a
   * form that has to be reset by hand every time it opens, in two codebases.
   */
  const [showAddPerson, setShowAddPerson] = useState(false);
  const openAddPerson = () => setShowAddPerson(true);

  /**
   * Register, then list — two calls, and the second is NOT rolled back if it fails.
   *
   * The enrolment is the expensive, irreversible half: the person exists and their vectors are
   * in the AI module. Throwing that away because a cheap, repeatable follow-up failed would be
   * the wrong trade, so the watchlist failure comes back as a WARNING rather than a rejection —
   * the dialog seals the form and offers Close, because pressing Register again would enrol the
   * same person twice.
   */
  const submitAddPerson = async ({ name, label, notes, files, watchlist }: PersonRegisterValue) => {
    const res: any = await registerNodePersonWithPhotos(
      nodeId,
      { name, label, notes: notes || undefined },
      files,
    );
    const personId = res?.person?.person_id;
    if (watchlist && personId) {
      try {
        await blacklistNodePerson(nodeId, personId, {
          ...watchlist,
          reason: watchlist.reason?.trim() || null,
        });
      } catch (err: any) {
        fetchRegisteredFaces();
        return {
          warning: `${name} was registered, but adding them to the watchlist failed: `
            + `${err.response?.data?.message || err.message}. `
            + 'List them from their card in this panel — the photos do not need uploading again.',
        };
      }
    }
    fetchRegisteredFaces();
  };

  /** Add more enrollment photos to a person — more angles make a better match. */
  const handleUploadPersonPhotos = async (personId: string, fileList: FileList | null) => {
    const files: File[] = fileList ? Array.from(fileList) : [];
    if (!files.length) return;
    if (files.length > 5) { alert('At most 5 photos at a time.'); return; }
    setPersonFacesLoading(true);
    try {
      await uploadNodePersonPhotos(nodeId, personId, files);
      // The AI module embeds asynchronously, so the new rows appear before the vectors do.
      setPersonFaces(await getNodePersonFaces(nodeId, personId));
      fetchRegisteredFaces();
    } catch (err: any) {
      alert('Upload failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setPersonFacesLoading(false);
    }
  };

  /** Mark / unmark a registered person as an employee, from the Registered People panel. */
  const handleToggleEmployee = async (person: any) => {
    setEmployeeBusyId(person.person_id);
    try {
      if (person.is_employee) {
        // The node refuses while attendance exists unless told to discard it, and answers 409
        // with the counts. Surfacing that as a confirm rather than an error is the point of it
        // being two steps: the operator is the one who knows whether those days matter.
        try {
          await unmarkNodePersonAsEmployee(nodeId, person.person_id);
        } catch (err: any) {
          if (err.response?.status !== 409) throw err;
          const msg = err.response?.data?.message;
          if (!confirm(`${msg}\n\nDelete that attendance history and unmark ${person.name}?`)) return;
          await unmarkNodePersonAsEmployee(nodeId, person.person_id, { discard_attendance: true });
        }
      } else {
        await markNodePersonAsEmployee(nodeId, person.person_id, { name: person.name });
      }
      fetchRegisteredFaces();
      if (model === 'face') fetchEvents();
    } catch (err: any) {
      alert('Could not change employment: ' + (err.response?.data?.message || err.message));
    } finally {
      setEmployeeBusyId(null);
    }
  };

  const handleRegister = async (linkToPersonId: string | null = null) => {
    if (!linkToPersonId && !registerName.trim()) { setRegisterError('Name is required'); return; }
    setRegisterLoading(true);
    setRegisterError('');
    try {
      const eventId = registerEventId(registerModal);
      if (linkToPersonId) {
        await linkNodePersonFromEvent(nodeId, linkToPersonId, { event_id: eventId });
      } else {
        const res: any = await registerNodePersonFromEvent(nodeId, {
          event_id: eventId,
          name: registerName.trim(),
          label: registerAsEmployee ? 'employee' : registerLabel,
          notes: registerNotes || undefined,
        });
        const personId = res?.person?.person_id;
        if (registerAsEmployee && personId) {
          try {
            await markNodePersonAsEmployee(nodeId, personId, {
              name: registerName.trim(),
              employee_code: registerEmployeeCode.trim() || undefined,
            });
          } catch (err: any) {
            setRegisterError(
              `${registerName.trim()} was registered, but marking them as an employee failed: `
              + `${err.response?.data?.message || err.message}. `
              + 'Mark them from the Registered People panel — the face does not need enrolling again.');
            setRegisterLoading(false);
            return;
          }
        }

        /**
         * The watchlist entry, in the same second-call shape as the employee promote above
         * and for the same reason: registration is the expensive, irreversible half — the
         * face is enrolled and the vectors are in the AI module — and throwing that away
         * because a cheap, repeatable follow-up failed would be the wrong trade. The message
         * says where to finish the job instead.
         */
        if (registerWatchlist && personId) {
          try {
            await blacklistNodePerson(nodeId, personId, {
              ...registerWatchlist,
              reason: registerWatchlist.reason?.trim() || null,
            });
          } catch (err: any) {
            setRegisterError(
              `${registerName.trim()} was registered, but adding them to the watchlist failed: `
              + `${err.response?.data?.message || err.message}. `
              + 'List them from the Registered People panel — the face does not need enrolling again.');
            setRegisterLoading(false);
            return;
          }
        }
      }
      setRegisterModal(null);
      fetchEvents();
      fetchRegisteredFaces();
    } catch (err: any) {
      setRegisterError(err.response?.data?.message || err.message);
    } finally {
      setRegisterLoading(false);
    }
  };

  /** The query the drawer's toolbar currently describes. */
  const registryParams = (page: number) => {
    const params: Record<string, string> = { limit: String(REGISTRY_PAGE), page: String(page) };
    if (registryQueryDebounced) params.search = registryQueryDebounced;
    if (registryFilter === 'employees') params.is_employee = 'true';
    if (registryFilter === 'watchlist') params.is_blacklisted = 'true';
    if (registryFilter === 'watchlist' && registryCategory) {
      params.blacklist_category = registryCategory;
    }
    return params;
  };

  /**
   * One page of the registry, as the drawer's toolbar currently describes it.
   *
   * The filters are the node's own (`search`, `is_employee`, `is_blacklisted`): searching the
   * loaded page instead would answer "not registered" about somebody who is registered but sits
   * past the limit — which is what the flat fetch of 100 used to do silently.
   *
   * It owns `registryPage` rather than trusting callers to keep it in step. Everything that
   * refreshes the drawer after an edit — flagged, deleted, promoted, photos added — calls this
   * with no argument, and a page counter left at 3 behind a list that had just been replaced
   * with page 1 would make the next "Load more" append page 4 over a two-hundred-person hole.
   */
  const fetchRegisteredFaces = async (page = 1) => {
    if (!nodeId) { setRegisteredFaces([]); setRegistryTotal(0); return; }
    setRegisteredLoading(true);
    try {
      const { rows, total } = await getNodePersonsPage(nodeId, registryParams(page));
      // Page 1 replaces (a new question), later pages append (the same question, more of it).
      // Appending de-duplicates because the registry is ordered by `updated_at`: flag somebody
      // on page 1 while page 2 is being asked for and they slide across the boundary, arriving
      // twice. React would then render two cards under one key.
      setRegisteredFaces((prev) => {
        if (page === 1) return rows;
        const seen = new Set(prev.map((p: any) => p.person_id));
        return [...prev, ...rows.filter((r: any) => !seen.has(r.person_id))];
      });
      setRegistryTotal(total);
      mergeRoster(rows);
      setRegistryPage(page);
    } catch (err) {
      console.error('Failed to fetch registered persons:', err);
    } finally {
      setRegisteredLoading(false);
    }
  };

  const loadMoreRegistered = () => fetchRegisteredFaces(registryPage + 1);

  /**
   * Everyone, unfiltered — for the Identity column and the link picker, which are about the
   * registry rather than about the drawer. One request, rows only; it never touches the
   * drawer's own list.
   */
  const fetchPersonRoster = async () => {
    if (!nodeId) return;
    try {
      // Walked page by page because the node caps a page at 100: the old `limit: 500` was
      // granted 100 and the picker quietly could not offer person 101 onwards. Bounded at
      // ROSTER_MAX_PAGES so a large registry costs a handful of requests, not a hundred.
      for (let page = 1; page <= ROSTER_MAX_PAGES; page += 1) {
        const rows = await getNodePersons(nodeId, { limit: String(REGISTRY_PAGE), page: String(page) });
        mergeRoster(rows);
        if (rows.length < REGISTRY_PAGE) break;
      }
    } catch (err) {
      console.error('Failed to fetch the persons roster:', err);
    }
  };

  /**
   * The node's own words, without the tunnel's plumbing.
   *
   * Every failure relayed from a node arrives prefixed with "Local service error:", which is
   * useful in a log and wrong in a dialog that is asking a question.
   */
  const localMessage = (err: any): string =>
    String(err.response?.data?.message || err.message || '').replace(/^Local service error:\s*/, '');

  /**
   * Delete a registered person.
   *
   * THE SECOND CONFIRMATION IS NOT A FORMALITY. A person who is also an employee is pinned
   * in the registry by the employees foreign key, and deleting them takes the employment
   * record — and every attendance day and sighting hanging off it — with them. The node
   * answers 409 with those counts rather than guessing, so the count is put in front of
   * whoever clicked before anything is destroyed. Without it they only ever saw the raw
   * Postgres constraint error, which said nothing about the person being staff.
   */
  const handleDeleteRegistered = async (personId: string, name: string) => {
    if (!confirm(`Delete registered person "${name}"? Their face vectors are removed from the AI module.`)) return;
    try {
      await deleteNodePerson(nodeId, personId);
    } catch (err: any) {
      // 409 from this endpoint has one meaning: they are also an employee, and the node
      // wants the attendance loss acknowledged. The counts are in the node's message —
      // proxyCommand relays a failure's status and message but not its body, so the text is
      // shown rather than recomposed from fields that do not arrive.
      if (err.response?.status !== 409) {
        alert('Failed to delete: ' + (err.response?.data?.message || err.message));
        return;
      }
      const msg = localMessage(err) || `${name} is an employee.`;
      if (!confirm(`${msg}\n\nDelete ${name} and that record anyway?\n\n`
        + 'To keep the attendance history, cancel and unmark them as an employee instead.')) return;
      try {
        await deleteNodePerson(nodeId, personId, { discard_attendance: true });
      } catch (retryErr: any) {
        alert('Failed to delete: ' + (retryErr.response?.data?.message || retryErr.message));
        return;
      }
    }
    fetchRegisteredFaces();
    if (model === 'face') fetchEvents();
  };

  // Expand a person to show/manage their enrolled faces (for unlinking individual faces).
  const toggleFaces = async (personId: string) => {
    if (expandedPersonId === personId) {
      setExpandedPersonId(null);
      setPersonFaces([]);
      return;
    }
    setExpandedPersonId(personId);
    setPersonFacesLoading(true);
    try {
      setPersonFaces(await getNodePersonFaces(nodeId, personId));
    } catch (err) {
      console.error('Failed to load person faces:', err);
      setPersonFaces([]);
    } finally {
      setPersonFacesLoading(false);
    }
  };

  const handleUnlinkFace = async (personId: string, photoId: string) => {
    if (!confirm('Unlink this face? The AI module will drop the matching face vector.')) return;
    try {
      await unlinkNodePersonFace(nodeId, personId, photoId);
      setPersonFaces((prev) => prev.filter((p: any) => p.id !== photoId));
      fetchRegisteredFaces();
      if (model === 'face') fetchEvents();
    } catch (err: any) {
      alert('Failed to unlink: ' + (err.response?.data?.message || err.message));
    }
  };

  /**
   * The drawer's list follows its toolbar: open it, search it, filter it or ask for more, and
   * the node is asked again. Also re-runs on a node change, so the registry never belongs to
   * the node that was selected a moment ago.
   */
  useEffect(() => {
    if (!showRegisteredPanel) return;
    // Every one of these is a NEW question, so it starts at page 1 — carrying page 4 into a
    // fresh search would show its fourth hundred of nothing.
    fetchRegisteredFaces(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showRegisteredPanel, nodeId, registryQueryDebounced, registryFilter, registryCategory]);

  // Enrollment is async: the AI module acks over Redis (ai:faces:enroll:result) and the
  // command-center flips persons.vector_registered. Poll while the panel is open and any
  // person is still "Processing…", so the badge flips to "Registered" without a manual refresh.
  // Bounded (~60s) so a silent/unreachable AI module doesn't poll forever.
  const enrollPollRef = useRef(0);
  useEffect(() => {
    if (!showRegisteredPanel) { enrollPollRef.current = 0; return; }
    const anyProcessing = registeredFaces.some((p: any) => !p.vector_registered);
    // Paused once the list has been paged: the refresh re-fetches page 1, which would throw
    // away the pages the operator asked for while they were reading them. Enrolment still
    // finishes — the badge just waits for a reopen rather than the list jumping underneath.
    if (!anyProcessing || registryPage > 1 || enrollPollRef.current >= 15) return;
    const t = setTimeout(() => { enrollPollRef.current += 1; fetchRegisteredFaces(1); }, 4000);
    return () => clearTimeout(t);
  }, [showRegisteredPanel, registeredFaces, registryPage]);

  // ── Columns for current model ──
  // The table drops `detailOnly` columns — they're folded into another cell here and only
  // stand alone in the record. eventColumns() still returns them, so the detail panel is
  // unaffected: one definition, two audiences.
  const columns = (MODEL_COLUMNS[model] || MODEL_COLUMNS.person).filter((c) => !c.detailOnly);

  // ── Image types present on an event ──
  // Prefer image_urls (new object-storage pipeline) over legacy image_paths; either way
  // we only need the KEYS (fullFrame/personCrop/faceCrop) — the bytes are blob-fetched
  // through the tunnel by imageType, so the source field doesn't change the fetch.
  const parseImagePaths = (event: any): string[] => {
    const parse = (v: any) => {
      if (typeof v === 'string') { try { v = JSON.parse(v); } catch { return null; } }
      return v && typeof v === 'object' && Object.keys(v).length ? v : null;
    };
    const imgs = parse(event.image_urls) || parse(event.image_paths);
    if (!imgs) return [];
    return Object.keys(imgs).filter((k) => imgs[k]);
  };

  /**
   * The event's frame, streamed through the node tunnel.
   *
   * Same proxy the record and the table's thumbnails use — by model + event id + image type —
   * but ONE frame per card (`fullFrame` where the model produced one), and the shared resolver
   * holds each request until its card is on screen. A page of events is otherwise a page of
   * simultaneous round trips over a link that may be a long way away.
   *
   * A tunnel 404 or timeout still resolves as a (JSON/text) Blob, so only real images count.
   */
  const loadEventImage = useCallback(
    async (event: any): Promise<string | null> => {
      const types = parseImagePaths(event);
      const type = types.includes('fullFrame') ? 'fullFrame' : types[0];
      if (!nodeId || !model || !event?.id || !type) return null;
      try {
        const blob: any = await getNodeModelEventImage(nodeId, model, event.id, type);
        if (!blob || (blob.type && !blob.type.startsWith('image/'))) return null;
        return URL.createObjectURL(blob);
      } catch {
        return null;
      }
    },
    [nodeId, model],
  );

  // ── Render cell value ──
  const renderCell = (col: ColumnDef, event: any) => {
    const val = event[col.key];

    if (col.type === 'camera') {
      return cameraNameMap[val] || val?.slice(0, 8) || '—';
    }
    if (col.type === 'face_identity') {
      // Name over id, one cell. They were two columns with the id four cells away from the
      // person it belongs to, which is the wrong shape for a fact that only ever means
      // anything ALONGSIDE the name — and the id column was dead width on every Unknown row.
      const name = resolvePersonName(event);
      const raw = event.matched_person_id || event.person_id;
      const id = raw ? String(raw) : '';
      return (
        <span className="flex items-center gap-2">
          <UserCircleIcon className={`h-5 w-5 shrink-0 ${name ? 'text-green-500' : 'text-muted-foreground'}`} />
          <span className="min-w-0">
            <span className={`block truncate font-semibold ${name ? 'text-green-500' : 'text-muted-foreground'}`}>
              {name || 'Unknown'}
            </span>
            <span className="block truncate font-mono text-[11px] text-muted-foreground" title={id || undefined}>
              {id ? (id.length > 12 ? `${id.slice(0, 12)}…` : id) : '—'}
            </span>
          </span>
        </span>
      );
    }
    if (col.type === 'datetime') {
      // Time above date, not "13 Aug 02:22:07" on one line. Down a page of events the times
      // are what differ and the date is usually the same on every row — stacked, the times
      // form a column the eye can run down instead of a phrase it has to read into.
      if (!val) return <span className="text-muted-foreground">—</span>;
      const d = new Date(val * 1000);
      return (
        <span className="block leading-tight">
          <span className="block font-semibold tabular-nums text-foreground">
            {d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          </span>
          <span className="block text-[11px] text-muted-foreground">
            {d.toLocaleDateString([], { day: '2-digit', month: 'short', year: 'numeric' })}
          </span>
        </span>
      );
    }
    if (col.type === 'confidence') {
      // A bar beside the number. "83%" and "94%" are four characters apart and read as the
      // same thing at a glance; the bar is what makes a weak match look weak while scanning.
      if (!val) return <span className="text-muted-foreground">—</span>;
      const pct = Math.max(0, Math.min(100, Math.round(val * 100)));
      const tone = pct >= 85 ? 'bg-green-500' : pct >= 70 ? 'bg-amber-500' : 'bg-red-500';
      return (
        <span className="flex items-center gap-2.5">
          <span className="h-1.5 w-16 shrink-0 overflow-hidden rounded-full bg-muted">
            <span className={`block h-full rounded-full ${tone}`} style={{ width: `${pct}%` }} />
          </span>
          <span className="font-semibold tabular-nums text-foreground">{pct}%</span>
        </span>
      );
    }
    if (col.type === 'person_identity') {
      return val ? (
        <span className="flex items-center gap-1.5">
          <UserCircleIcon className="h-4 w-4 text-green-500" />
          <span className="font-medium text-green-400">{val}</span>
        </span>
      ) : (
        <span className="text-muted-foreground">—</span>
      );
    }
    if (col.type === 'face_status') {
      return val ? (
        <span className="whitespace-nowrap rounded-md px-2 py-1 text-[10px] font-bold uppercase bg-green-500/15 text-green-500">Known</span>
      ) : (
        <span className="whitespace-nowrap rounded-md px-2 py-1 text-[10px] font-bold uppercase bg-orange-500/15 text-orange-500">Unknown</span>
      );
    }
    if (col.type === 'vehicle_direction') {
      return val === 'entry' ? (
        <span className="whitespace-nowrap rounded-md px-2 py-1 text-[10px] font-bold uppercase bg-green-500/15 text-green-500">↓ Entry</span>
      ) : (
        <span className="whitespace-nowrap rounded-md px-2 py-1 text-[10px] font-bold uppercase bg-orange-500/15 text-orange-500">↑ Exit</span>
      );
    }
    if (col.type === 'inout') {
      if (val === 'in') return <span className="whitespace-nowrap rounded-md px-2 py-1 text-[10px] font-bold uppercase bg-green-500/15 text-green-500">↓ In</span>;
      if (val === 'out') return <span className="whitespace-nowrap rounded-md px-2 py-1 text-[10px] font-bold uppercase bg-red-500/15 text-red-500">↑ Out</span>;
      return <span className="text-muted-foreground">—</span>;
    }
    if (col.type === 'person_id') {
      // Prefer the registered identity id (matched_person_id, a UUID) over the AI's transient
      // per-track detection id (e.g. "person1"), which is meaningless once a face is registered.
      const raw = event.matched_person_id || val;
      if (!raw) return <span className="text-muted-foreground">—</span>;
      const id = String(raw);
      return <span className="font-mono text-xs cursor-default" title={id}>{id.slice(0, 8)}{id.length > 8 ? '…' : ''}</span>;
    }
    if (col.type === 'plate_number') {
      if (!val) return <span className="text-muted-foreground text-xs">—</span>;
      return (
        <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
          <span className="font-mono font-semibold text-foreground">{val}</span>
          {/* The verdict recorded when the crossing was stored — see the node's migration 052.
              Shown on the plate rather than as its own column: it is a fact ABOUT this plate,
              and a tenth column would push the counts off the visible width. */}
          {event.is_blacklisted && (
            <span className="whitespace-nowrap rounded-md bg-red-500/15 px-2 py-1 text-[10px] font-bold uppercase text-red-500">
              Blacklisted
            </span>
          )}
          {/* Always visible rather than hover-revealed: renderCell serves the card grid as
              well as the table, and a `group-hover:` here would depend on a wrapper the cards
              do not have — leaving the control permanently invisible in one of the two views. */}
          {canManageWatchlist && !event.is_blacklisted && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                // Opening the panel is part of the gesture: seeding a form inside a drawer
                // that is not on screen looks like the button did nothing.
                setShowBlacklistPanel(true);
                setSeedPlate(String(val));
              }}
              title="Add this plate to the vehicle watchlist"
              className="rounded p-0.5 text-muted-foreground transition-colors hover:text-destructive"
            >
              <NoSymbolIcon className="h-3.5 w-3.5" />
            </button>
          )}
        </span>
      );
    }
    if (col.type === 'plate_status') {
      if (!event.has_anpr) return <span className="text-muted-foreground text-xs">No ANPR</span>;
      const statusColor = val === 'valid' ? 'bg-green-500/15 text-green-500' : val === 'partial' ? 'bg-amber-500/15 text-amber-500' : 'bg-red-500/15 text-red-500';
      return <span className={`whitespace-nowrap rounded-md px-2 py-1 text-[10px] font-bold uppercase ${statusColor}`}>{val || 'detected'}</span>;
    }
    if (col.type === 'ppe_violation_type') {
      const color = (val || '').toLowerCase().includes('no-') ? 'bg-red-500/15 text-red-500' : 'bg-amber-500/15 text-amber-500';
      return <span className={`whitespace-nowrap rounded-md px-2 py-1 text-[10px] font-bold uppercase ${color}`}>{val || '—'}</span>;
    }
    if (col.type === 'detection_type') {
      // Red for flame, slate for smoke. Deliberately NOT both amber: these two rows
      // mean different things and the whole point of the column is telling them
      // apart at a glance.
      const kind = (val || '').toLowerCase();
      const fireColor = kind === 'fire' ? 'bg-red-500/15 text-red-500'
        : kind === 'smoke' ? 'bg-slate-500/15 text-slate-400'
        : 'bg-amber-500/15 text-amber-500';
      return <span className={`whitespace-nowrap rounded-md px-2 py-1 text-[10px] font-bold uppercase ${fireColor}`}>{val || 'unknown'}</span>;
    }
    if (col.type === 'theft_type') {
      const theftColor = (val || '').toLowerCase().includes('shoplifting') ? 'bg-red-500/15 text-red-500' : 'bg-amber-500/15 text-amber-500';
      return <span className={`whitespace-nowrap rounded-md px-2 py-1 text-[10px] font-bold uppercase ${theftColor}`}>{val || '—'}</span>;
    }
    // if (col.type === 'face_action') {
    //   return !event.is_known && canEdit ? (
    //     <button
    //       onClick={(e) => { e.stopPropagation(); openRegisterModal(event); }}
    //       className="flex items-center gap-1.5 rounded-lg bg-brand/10 px-2.5 py-1.5 text-[12px] font-semibold text-brand hover:bg-brand/20 transition-colors"
    //       title="Register this face"
    //     >
    //       <UserPlusIcon className="h-3.5 w-3.5" /> Register
    //     </button>
    //   ) : (
    //     <span className="text-[11px] text-muted-foreground">✓ Registered</span>
    //   );
    // }

    if (col.type === 'face_action') {
      if (event.is_known) {
        return <span className="whitespace-nowrap text-[12px] text-muted-foreground">✓ Registered</span>;
      }
      if (canEdit) {
        return (
          <button
            onClick={(e) => { e.stopPropagation(); openRegisterModal(event); }}
            className="flex items-center gap-1.5 rounded-lg bg-brand/10 px-2.5 py-1.5 text-[12px] font-semibold text-brand hover:bg-brand/20 transition-colors"
            title="Register this face"
          >
            <UserPlusIcon className="h-3.5 w-3.5" /> Register
          </button>
        );
      }
      return <span className="whitespace-nowrap text-[12px] text-muted-foreground">✗ Not Registered</span>;
    }
    if (col.type === 'images') {
      const types = parseImagePaths(event);
      if (types.length === 0) return '—';
      // fullFrame LEADS. Every model that produces one produces crops beside it, and the wide
      // shot is the one that says where the thing was — a crop of a face is unreadable as a
      // scene, and the reader can't tell which camera's yard they are looking at.
      const ordered = types.includes('fullFrame')
        ? ['fullFrame', ...types.filter((t) => t !== 'fullFrame')]
        : types;

      // One frame is the scene, at full size. Anything past it is a crop OUT of that scene —
      // a detail, not a peer — so it rides beside the lead as a thumbnail.
      const spread = ordered.length <= FRAMES_BIG;
      const rest = spread ? [] : ordered.slice(1);
      const smalls = rest.slice(0, FRAMES_SMALL);
      const extra = rest.length - smalls.length;

      const tile = (imgType: string, small: boolean) => (
        <AuthImage
          key={imgType}
          nodeId={nodeId}
          model={model}
          eventId={event.id}
          imageType={imgType}
          small={small}
          onClick={() => setPreviewEvent({ eventId: event.id, imageType: imgType })}
        />
      );

      return (
        <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
          {(spread ? ordered : ordered.slice(0, 1)).map((t) => tile(t, false))}
          {smalls.length > 0 && (
            // Two rows, filled column by column: the stack is then about as tall as the lead
            // frame beside it instead of a long tail running off the side of the table.
            <div className="grid grid-flow-col grid-rows-2 gap-1">
              {smalls.map((t) => tile(t, true))}
              {extra > 0 && (
                <span className={FRAME_MORE} title={`${extra} more frame${extra === 1 ? '' : 's'} on the record`}>
                  +{extra}
                </span>
              )}
            </div>
          )}
        </div>
      );
    }
    if (col.render) return col.render(val, event);
    return val || '—';
  };

  const filterCol = useFilterColumn(lockedModel ? 'reports' : 'alert-investigation');
  const activeFilters = [groupId, cameraId, dateFrom, dateTo, search, flaggedOnly].filter(Boolean).length;
  const clearAllFilters = () => { setGroupId(''); setCameraId(''); setDateFrom(''); setDateTo(''); setSearch(''); setFlaggedOnly(false); setFocusEventId(''); setPage(1); };

  return (
    <>
      <PageTitle
        title={lockedModel ? reportModelLabel(lockedModel) : 'Alert Investigation'}
        subtitle={total > 0 ? `${total} events` : undefined}
      >
        {model === 'face' && nodeId && (
          <button
            data-tour="report-registered-people"
            onClick={() => setShowRegisteredPanel(!showRegisteredPanel)}
            className="flex h-8 items-center gap-1.5 rounded-lg border border-border px-3 text-[12px] font-medium text-foreground hover:bg-muted transition-colors"
            title="Manage registered people and who is an employee"
          >
            <UserCircleIcon className="h-4 w-4" /> Registered People
          </button>
        )}
        {/* `nodeId &&` for the same reason the faces button has it: the watchlist lives on a
            node, so with none selected there is nothing to open. */}
        {model === 'vehicle_crossing' && nodeId && (
          <button
            data-tour="report-blacklist"
            onClick={() => setShowBlacklistPanel(!showBlacklistPanel)}
            className="flex h-8 items-center gap-1.5 rounded-lg border border-border px-3 text-[12px] font-medium text-foreground hover:bg-muted transition-colors"
            title="Register and manage blacklisted vehicles"
          >
            <NoSymbolIcon className="h-4 w-4" /> Blacklisted Vehicles
          </button>
        )}
        <button
          data-tour="report-refresh"
          onClick={fetchEvents}
          className="flex h-8 w-8 items-center justify-center rounded-lg border border-border text-muted-foreground hover:bg-muted transition-colors"
          title="Refresh"
        >
          <ArrowPathIcon className="h-4 w-4" />
        </button>
      </PageTitle>

      <PageWithFilters
        filters={(
          <FilterColumn
            open={filterCol.open}
            onToggle={filterCol.toggle}
            label="Event filters"
            activeCount={activeFilters}
            onClearAll={clearAllFilters}
          >
            <FilterSearch value={search} onChange={(v) => { setSearch(v); setPage(1); }} placeholder="Search..." />

            {/* AI Model — hidden on a single-model Reports page */}
            {!lockedModel && (
              <FilterSection title="AI model">
                <select
                  value={model}
                  onChange={(e) => { setModel(e.target.value); setPage(1); }}
                  className={FILTER_CONTROL}
                >
                  {AI_MODELS.map((m) => (
                    <option key={m.key} value={m.key}>{m.label}</option>
                  ))}
                </select>
              </FilterSection>
            )}

            <FilterSection title="Group">
              <select
                value={groupId}
                onChange={(e) => { setGroupId(e.target.value); setCameraId(''); setPage(1); }}
                className={FILTER_CONTROL}
              >
                <option value="">All Groups</option>
                {groups.map((g: any) => (
                  <option key={g.id} value={g.id}>{g.name}</option>
                ))}
              </select>
            </FilterSection>

            <FilterSection title="Camera">
              <select
                value={cameraId}
                data-tour="report-camera"
                onChange={(e) => { setCameraId(e.target.value); setPage(1); }}
                className={FILTER_CONTROL}
              >
                <option value="">All Cameras</option>
                {filteredCameras.map((c: any) => (
                  <option key={c.id} value={c.id}>{c.name || c.id}</option>
                ))}
              </select>
            </FilterSection>

            <FilterSection title="Date range">
              <div data-tour="report-date-range" className="space-y-1.5">
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                  className={FILTER_CONTROL}
                  aria-label="From"
                />
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                  className={FILTER_CONTROL}
                  aria-label="To"
                />
              </div>
            </FilterSection>

            {/* Watchlist filter — Vehicle Crossing only, because is_blacklisted is a column on
                vehicle_crossing_events and nothing else. Offered here rather than inside the
                Blacklisted Vehicles panel: it narrows the TABLE, so it belongs with the table's
                other filters, and it stays usable with the panel closed. */}
            {model === 'vehicle_crossing' && (
              <FilterSection title="Watchlist">
                <FilterCheck
                  checked={flaggedOnly}
                  onClick={() => { setFlaggedOnly((v) => !v); setPage(1); }}
                  label={<span className="inline-flex items-center gap-1.5" title="Show only crossings whose plate was on the watchlist when it was read"><NoSymbolIcon className="h-4 w-4" /> Blacklisted only</span>}
                />
              </FilterSection>
            )}

            {/* Rows per page. Not wrapped in a <label> — a label around a native select
                re-dispatches the click to the control in some Chromium builds, which opens the
                dropdown and closes it again so the choice never lands. */}
            <FilterSection title="Rows per page">
              <select
                value={String(pageSize)}
                onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                aria-label="Rows per page"
                className={FILTER_CONTROL}
              >
                {EVENTS_PAGE_SIZE_OPTIONS.map((n) => <option key={n} value={String(n)}>{n}</option>)}
              </select>
            </FilterSection>
          </FilterColumn>
        )}
      >
      {/* Arrived from a deep link (`?event_id=…`). Said out loud, because a one-row table
          is otherwise indistinguishable from a filter that matched almost nothing. */}
      {focusEventId && (
        <div>
          <span className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-brand/40 bg-brand/10 px-2.5 text-[12px] font-medium text-brand">
            Showing one event
            <button
              onClick={() => { setFocusEventId(''); setPage(1); }}
              title="Show all events"
              aria-label="Show all events"
              className="rounded p-0.5 hover:bg-brand/20 transition-colors"
            >
              <XMarkIcon className="h-3.5 w-3.5" />
            </button>
          </span>
        </div>
      )}

      {/* ── Model Tab Indicator — replaced by the Reports sub-menu when locked ── */}
      {!lockedModel && (
        <div className="flex items-center gap-1 border-b border-border overflow-x-auto">
          {AI_MODELS.map((m) => (
            <button
              key={m.key}
              onClick={() => { setModel(m.key); setPage(1); }}
              className={`px-4 py-2 text-[13px] font-medium border-b-2 transition-colors whitespace-nowrap ${model === m.key
                ? 'border-brand text-brand'
                : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
            >
              {m.label}
            </button>
          ))}
        </div>
      )}

      {/* ── No site selected ── */}
      {!nodeId ? (
        <div className="rounded-xl border border-border bg-card shadow-card py-16 text-center text-muted-foreground text-sm">
          Select a site from the sidebar to investigate alerts.
        </div>
      ) : (
        /* ── Events ── */
        <div data-tour="report-results" className="rounded-xl border border-border bg-card shadow-card overflow-hidden">
          {/* The view switch sits on the results card itself: it changes how THIS list is
              drawn, not what is in it. */}
          <div data-tour="report-view" className="flex items-center justify-end border-b border-border px-3 py-2">
            <SegmentedToggle<'cards' | 'table' | 'chart'>
              aria-label="Report view"
              value={effectiveView}
              onChange={setView}
              options={[
                /* Chart FIRST, and only where there is one — it is Person Count's default, and a
                   default that sits third in its own switch reads as an afterthought. */
                ...(chartable
                  ? [{
                      key: 'chart' as const,
                      label: 'Chart',
                      icon: ChartBarIcon,
                      title: 'Chart — occupancy over time, per camera',
                    }]
                  : []),
                { key: 'cards' as const, label: 'Cards', icon: Squares2X2Icon, title: 'Cards — the event’s own frame' },
                { key: 'table' as const, label: 'Table', icon: ListBulletIcon, title: 'Table — more events per screen' },
              ]}
            />
          </div>

          {effectiveView === 'chart' ? (
            /* THE PERSON COUNT REPORT. The log is still behind the Cards/Table tabs — this is the
               instrument the model's output actually calls for, not a replacement for its records.
               It carries no pager: a series is the whole window at once, which is the point. */
            <OccupancyReport
              data={occupancy}
              loading={occupancyLoading}
              error={occupancyError}
              cameraName={(id) => cameraNameMap[id]}
            />
          ) : effectiveView === 'cards' ? (
            <EventListCards
              events={events}
              columns={columns}
              loading={loading}
              context={{ cameraName: (id: string) => cameraNameMap[id], personName: resolvePersonName }}
              loadImage={loadEventImage}
              /* A face event's headline is WHO, not "Face Recognized" — that's the whole
                 question the model was asked. Every other model keeps the event type. */
              titleFor={model === 'face' ? (e: any) => resolvePersonName(e) || 'Unknown face' : undefined}
              onOpen={setSelectedEventIndex}
              cardAction={
                model === 'face' && canEdit
                  ? (e: any) =>
                      e.is_known ? (
                        <span className="px-1 text-[12px] text-muted-foreground">✓ Registered</span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => openRegisterModal(e)}
                          className="flex items-center gap-1.5 rounded-md bg-brand/10 px-2.5 py-1 text-[12px] font-semibold text-brand transition-colors hover:bg-brand/20"
                          title="Register this face"
                        >
                          <UserPlusIcon className="h-3.5 w-3.5" /> Register
                        </button>
                      )
                  : undefined
              }
              emptyTitle={
                focusEventId
                  ? 'That event is no longer available — it may have been deleted or passed its retention window.'
                  : 'No events found for the selected filters.'
              }
            />
          ) : (
          <div className="overflow-x-auto">
            {loading ? (
              <div className="p-6 space-y-3">
                {/* As tall as a real row — the frames in it set that height, and 48px skeletons
                    followed by 80px rows read as the list jumping when it loads. */}
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="h-20 rounded-lg bg-muted animate-pulse" />
                ))}
              </div>
            ) : events.length === 0 ? (
              <div className="py-16 text-center text-muted-foreground text-sm">
                {focusEventId
                  // A dead deep link is its own failure: the row was deleted or aged out of
                  // retention. "No events for the selected filters" would send the reader
                  // hunting through a filter bar that isn't the reason.
                  ? 'That event is no longer available — it may have been deleted or passed its retention window.'
                  : 'No events found for the selected filters.'}
              </div>
            ) : (
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    {columns.map((col) => (
                      <th
                        key={col.key}
                        className="whitespace-nowrap px-4 py-3.5 text-left text-[11px] font-bold uppercase tracking-[0.08em] text-muted-foreground"
                      >
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {events.map((event, idx) => (
                    <tr
                      key={event.id || idx}
                      onClick={() => setSelectedEventIndex(idx)}
                      className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors cursor-pointer"
                    >
                      {columns.map((col) => (
                        <td key={col.key} className="whitespace-nowrap px-4 py-3 align-middle text-foreground">
                          {renderCell(col, event)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          )}

          {/* Pagination. Never over the chart: a series IS the whole window, so a page counter
              under it would be describing the log the reader is not currently looking at. */}
          {effectiveView !== 'chart' && events.length > 0 && (
            <div className="flex items-center justify-between border-t border-border px-4 py-3">
              <span className="text-[12px] text-muted-foreground">
                Page {page} of {totalPages} · {Number(total || 0).toLocaleString()} total events · {pageSize} per page
              </span>
              <div className="flex items-center gap-2">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                  className="flex h-8 items-center gap-1 rounded-lg border border-border px-3 text-[12px] text-foreground hover:bg-muted disabled:opacity-30 transition-colors"
                >
                  <ChevronLeftIcon className="h-3.5 w-3.5" /> Prev
                </button>
                <button
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className="flex h-8 items-center gap-1 rounded-lg border border-border px-3 text-[12px] text-foreground hover:bg-muted disabled:opacity-30 transition-colors"
                >
                  Next <ChevronRightIcon className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      </PageWithFilters>

      {/* ── Image Preview Modal ── */}
      {previewEvent && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
          onClick={() => setPreviewEvent(null)}
        >
          <div className="relative max-w-3xl max-h-[80vh]" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setPreviewEvent(null)}
              className="absolute -top-3 -right-3 flex h-8 w-8 items-center justify-center rounded-full bg-card border border-border shadow-card"
            >
              <XMarkIcon className="h-4 w-4 text-foreground" />
            </button>
            <AuthPreviewImage nodeId={nodeId} model={model} eventId={previewEvent.eventId} imageType={previewEvent.imageType} />
          </div>
        </div>
      )}

      {/* ── Event Detail Modal ──
          The common Modal shell (the same one the alert board opens an alert in) at its full
          width, because the record IS the snapshot. No header of its own: the record draws one,
          as the alert record does, and carries the pager, "Full page" and close in it.
          Left/right step through the table's rows without closing it — see RecordPager. */}
      <Modal
        open={!!selectedEvent}
        onClose={() => setSelectedEventIndex(null)}
        size="full"
        ariaLabel="Event details"
      >
        {selectedEvent && (
          <EventDetailBody
            event={selectedEvent}
            model={model}
            nodeId={nodeId}
            cameraNameMap={cameraNameMap}
            personName={resolvePersonName}
            onImageClick={(imageType) => setPreviewEvent({ eventId: selectedEvent.id, imageType })}
            hint="← → move between events · ↑ ↓ move between this event’s frames"
            onClose={() => setSelectedEventIndex(null)}
            headerActions={(
              <>
                <RecordPager
                  variant="boxed"
                  index={selectedEventIndex ?? 0}
                  count={events.length}
                  onIndexChange={setSelectedEventIndex}
                  noun="events"
                />
                <RecordFullPageButton
                  what="event"
                  onClick={() => navigate(`/events/${reportModelSlug(model)}/${selectedEvent.event_id || selectedEvent.id}`)}
                />
              </>
            )}
            actions={model === 'face' && !selectedEvent.is_known && canEdit ? (
              <button
                onClick={() => { setSelectedEventIndex(null); openRegisterModal(selectedEvent); }}
                className="flex h-12 items-center gap-2 rounded-xl bg-brand px-4 text-[15px] font-semibold text-white transition-colors hover:bg-brand-dark"
              >
                <UserPlusIcon className="h-5 w-5" /> Register This Face
              </button>
            ) : null}
          />
        )}
      </Modal>


      {/* ── Face Registration Modal ── */}
      {registerModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setRegisterModal(null)}>
          <div className="w-full max-w-md rounded-2xl border border-border bg-card shadow-2xl m-4 p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-semibold text-foreground">Register Face</h2>
              <button onClick={() => setRegisterModal(null)} className="flex h-8 w-8 items-center justify-center rounded-lg border border-border text-muted-foreground hover:bg-muted">
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>

            {/* Face preview */}
            {(() => {
              const imageTypes = parseImagePaths(registerModal);
              const faceType = imageTypes.find(t => t.includes('face')) || imageTypes[0];
              if (!faceType) return null;
              return (
                <div className="mb-4 flex justify-center">
                  <AuthImage nodeId={nodeId} model="face" eventId={registerModal.id} imageType={faceType} />
                </div>
              );
            })()}

            <div className="space-y-3">
              {/* Link to an existing registered person (vectors owned by the AI module) */}
              {/* The roster, not the drawer's page: which people a face may be linked to has
                  nothing to do with how the drawer behind this modal is filtered. */}
              {rosterList.length > 0 && (
                <div className="rounded-lg border border-border bg-muted p-3">
                  <p className="text-[12px] text-muted-foreground mb-2">Link to an existing person?</p>
                  <input
                    type="text"
                    value={linkFilter}
                    onChange={(e) => setLinkFilter(e.target.value)}
                    placeholder="Filter registered people..."
                    className="w-full h-8 mb-2 rounded-md border border-border bg-background px-2.5 text-[12px] text-foreground focus:border-brand focus:outline-none"
                  />
                  <div className="space-y-1.5 max-h-40 overflow-y-auto">
                    {rosterList
                      .filter((p: any) => !linkFilter || (p.name || '').toLowerCase().includes(linkFilter.toLowerCase()))
                      .slice(0, 20)
                      .map((person: any) => (
                        <div key={person.person_id} className="flex items-center justify-between rounded-md border border-border bg-background px-3 py-1.5">
                          <div className="flex items-center gap-2 min-w-0">
                            <UserCircleIcon className="h-7 w-7 shrink-0 text-muted-foreground" />
                            <div className="min-w-0">
                              <span className="block text-[12px] font-medium text-foreground truncate">{person.name}</span>
                              <span className="text-[11px] text-muted-foreground">{person.label}</span>
                            </div>
                          </div>
                          <button
                            onClick={() => handleRegister(person.person_id)}
                            disabled={registerLoading}
                            className="h-7 px-2.5 rounded-md bg-brand text-white text-[11px] font-medium hover:bg-brand/90 disabled:opacity-50 transition-colors"
                          >
                            Link
                          </button>
                        </div>
                      ))}
                  </div>
                  <p className="mt-2 text-[11px] text-muted-foreground">Or register as new below.</p>
                </div>
              )}

              <div>
                <label className="block text-[12px] font-medium text-muted-foreground mb-1">Name *</label>
                <input
                  type="text"
                  value={registerName}
                  onChange={(e) => setRegisterName(e.target.value)}
                  placeholder="Enter person's name"
                  className="w-full h-9 rounded-lg border border-border bg-background px-3 text-[13px] text-foreground focus:border-brand focus:outline-none"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-[12px] font-medium text-muted-foreground mb-1">Label</label>
                <select
                  value={registerLabel}
                  onChange={(e) => setRegisterLabel(e.target.value)}
                  className="w-full h-9 rounded-lg border border-border bg-background px-3 text-[13px] text-foreground focus:border-brand focus:outline-none"
                >
                  <option value="visitor">Visitor</option>
                  <option value="vip">VIP</option>
                  <option value="contractor">Contractor</option>
                  <option value="other">Other</option>
                </select>
                {/* "Employee" is deliberately NOT in this list. It is not a kind of person the
                    way a visitor or a VIP is — it is an employment record attached to one, and
                    putting it here made it a choice you could only make once, at registration. */}
              </div>
              <div>
                <label className="block text-[12px] font-medium text-muted-foreground mb-1">Notes</label>
                <input
                  type="text"
                  value={registerNotes}
                  onChange={(e) => setRegisterNotes(e.target.value)}
                  placeholder="Optional notes..."
                  className="w-full h-9 rounded-lg border border-border bg-background px-3 text-[13px] text-foreground focus:border-brand focus:outline-none"
                />
              </div>

              {/* Employment, as a classification of the person being registered. Ticking it now
                  and marking them later from the Registered People panel do exactly the same
                  thing, so nothing is lost by leaving it unticked. */}
              <div className="rounded-lg border border-border bg-muted p-3">
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={registerAsEmployee}
                    onChange={(e) => setRegisterAsEmployee(e.target.checked)}
                    className="mt-0.5 h-4 w-4 accent-[color:var(--brand)]"
                  />
                  <span className="min-w-0">
                    <span className="block text-[13px] font-medium text-foreground">
                      This person is an employee
                    </span>
                    <span className="block text-[11px] text-muted-foreground">
                      {registerAsEmployee
                        ? 'Attendance will be calculated from now on.'
                        : 'Registered only — recognised, with an in/out log and no attendance. '
                          + 'You can mark them an employee at any time.'}
                    </span>
                  </span>
                </label>

                {registerAsEmployee && (
                  <input
                    type="text"
                    value={registerEmployeeCode}
                    onChange={(e) => setRegisterEmployeeCode(e.target.value)}
                    placeholder="Employee code (optional)"
                    className="mt-2.5 w-full h-9 rounded-lg border border-border bg-background px-3 text-[13px] text-foreground focus:border-brand focus:outline-none"
                  />
                )}
              </div>

              {/*
                And the watchlist, at the moment the person is created.
                Flagging somebody used to mean registering them first, finding them again in
                the Registered People drawer, then opening the dialog — and between the first
                step and the third they are registered and NOT flagged, which for the one case
                this modal is most often opened for (a face that should never have been on
                site) is exactly the wrong way round.
                Gated on the same permission as the drawer's own watchlist controls: being
                able to register somebody is not being able to decide who gets interrupted.
              */}
              {canManageWatchlist && (
                <PersonWatchlistToggle
                  value={registerWatchlist}
                  onChange={setRegisterWatchlist}
                  name={registerName}
                  disabled={registerLoading}
                />
              )}

              {registerError && (
                <p className="text-[12px] text-red-500 bg-red-500/10 rounded-lg px-3 py-2">{registerError}</p>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <button
                  onClick={() => setRegisterModal(null)}
                  className="h-9 px-4 rounded-lg border border-border text-[13px] text-foreground hover:bg-muted transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleRegister(null)}
                  disabled={registerLoading || !registerName.trim()}
                  className="h-9 px-4 rounded-lg bg-brand text-white text-[13px] font-medium hover:bg-brand/90 disabled:opacity-50 transition-colors"
                >
                  {registerLoading ? 'Registering...' : 'Register as New'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Blacklisted Vehicles Panel ──
          The same right-side drawer as Registered Faces above, because it is the same kind of
          thing: an operator-maintained registry of identities that changes what an AI event
          means, edited in response to the report behind it. Overlaying rather than displacing
          leaves the crossings — and the filters and scroll position the operator arrived with —
          untouched underneath.

          Rendered only while open, so the list is not fetched on every visit to a report whose
          watchlist most visits never look at.

          The list belongs to the NODE that matches against it, so the `key` remounts it
          when the operator switches node. */}
      {model === 'vehicle_crossing' && showBlacklistPanel && (
        <VehicleBlacklistPanel
          key={nodeId || 'no-node'}
          seedPlate={seedPlate}
          onSeedConsumed={() => setSeedPlate(null)}
          onChanged={() => setBlacklistVersion((v) => v + 1)}
          canEdit={canManageWatchlist}
          onClose={() => setShowBlacklistPanel(false)}
        />
      )}

      {/*
        ── The person watchlist dialog ──
        Outside the Registered Faces panel, not inside it: the panel scrolls and can be closed,
        and a dialog that lives in a scrolling drawer goes with it.
      */}
      <PersonBlacklistDialog
        person={blacklistTarget}
        onClose={() => setBlacklistTarget(null)}
        onSubmit={async (value) => {
          await blacklistNodePerson(nodeId, blacklistTarget.person_id, value);
          afterWatchlistChange();
        }}
        onRemove={async () => {
          await unblacklistNodePerson(nodeId, blacklistTarget.person_id);
          afterWatchlistChange();
        }}
      />

      {/*
        ── Register a person from photographs ──
        A dialog, and mounted out here beside the watchlist one rather than inside the drawer:
        a form that lives in a scrolling panel scrolls away from the operator filling it in,
        and closes with the panel. See PersonRegisterDialog.
      */}
      <PersonRegisterDialog
        open={showAddPerson && canEdit && !!nodeId}
        onClose={() => setShowAddPerson(false)}
        onSubmit={submitAddPerson}
        canManageWatchlist={canManageWatchlist}
      />

      {/* ── Registered Faces Panel ── */}
      {showRegisteredPanel && (
        <div className="fixed inset-y-0 right-0 z-50 w-full max-w-md border-l border-border bg-card shadow-2xl overflow-y-auto">
          {/* `bg-card/95` here was invisible ink: the theme's colours are bare `var()`, and
              Tailwind v3 emits no rule at all for an opacity modifier on one of those — the
              sticky header had NO background and the cards scrolled through it. The vehicle
              watchlist drawer had the same bug. */}
          <div className="sticky top-0 z-10 border-b border-border bg-card backdrop-blur px-5 py-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-foreground">Registered People</h2>
              <button
                onClick={() => setShowRegisteredPanel(false)}
                className="flex h-8 w-8 items-center justify-center rounded-lg border border-border text-muted-foreground hover:bg-muted"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
            {/* No longer hidden while the form is open: the form is a dialog over this panel
                now, so hiding the button behind it only made the header jump. */}
            {canEdit && nodeId && (
              <button
                onClick={openAddPerson}
                className="mt-3 flex h-9 w-full items-center justify-center gap-1.5 rounded-lg bg-brand text-[13px] font-medium text-white hover:bg-brand/90 transition-colors"
              >
                <UserPlusIcon className="h-4 w-4" /> Register Person
              </button>
            )}

            {/* Search + filter + the node's own count. In the sticky header, not the scrolling
                body: the controls that decide what the list contains have to stay reachable
                from the bottom of a list of a hundred cards. */}
            <RegisteredPeopleToolbar
              query={registryQuery}
              onQueryChange={setRegistryQuery}
              filter={registryFilter}
              onFilterChange={(f) => {
                setRegistryFilter(f);
                if (f !== 'watchlist') setRegistryCategory('');
              }}
              category={registryCategory}
              onCategoryChange={setRegistryCategory}
              shown={registeredFaces.length}
              total={registryTotal}
              loading={registeredLoading}
            />
          </div>

          <div className="p-5">
            {registeredLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => <div key={i} className="h-16 rounded-lg bg-muted animate-pulse" />)}
              </div>
            ) : registeredFaces.length === 0 ? (
              /* Empty because of the filter and empty because nobody is registered are
                 different facts, and only one of them is the operator's to fix. */
              <p className="text-center text-muted-foreground text-sm py-8">
                {registryQueryDebounced || registryFilter !== 'all' || registryCategory
                  ? 'Nobody in the registry matches that.'
                  : 'Nobody is registered yet. Register a face from an event in the table.'}
              </p>
            ) : (
              <div className="space-y-3">
                {registeredFaces.map((person: any) => (
                  <RegisteredPersonCard
                    key={person.person_id}
                    person={person}
                    canEdit={canEdit}
                    expanded={expandedPersonId === person.person_id}
                    busy={employeeBusyId === person.person_id}
                    onToggleExpand={() => toggleFaces(person.person_id)}
                    onToggleEmployee={() => handleToggleEmployee(person)}
                    onToggleBlacklist={() => setBlacklistTarget(person)}
                    onDelete={() => handleDeleteRegistered(person.person_id, person.name)}
                  >
                    <EnrolledFacesList
                      faces={personFaces}
                      loading={personFacesLoading}
                      canEdit={canEdit}
                      onUpload={(files) => handleUploadPersonPhotos(person.person_id, files)}
                      onUnlink={(photoId) => handleUnlinkFace(person.person_id, photoId)}
                    />
                  </RegisteredPersonCard>
                ))}
                <PeopleLoadMore
                  shown={registeredFaces.length}
                  total={registryTotal}
                  loading={registeredLoading}
                  onLoadMore={loadMoreRegistered}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
